<template>
  <ValidationObserver ref="observer">
    <div v-if="loadMenu" :class="{ 'menu-page-scroll': !embedded }">
      <b-card class="preview-setup-card mb-3 shadow preview-workflow-card" body-class="preview-setup-body">
        <!-- <header class="preview-workflow-hero mb-2">
          <h5 class="preview-workflow-hero__title mb-0">Chart preview workspace</h5>
          <p class="preview-workflow-hero__sub text-muted mb-0">
            Choose SQL or table mode, review the chart, then tune variable mapping before saving.
          </p>
        </header> -->

        <section class="preview-workflow-section mb-2" aria-labelledby="preview-step-data-source">
          <div class="preview-section-heading preview-section-heading--data-source">
            <div class="data-source-toolbar__start">
              <div
                class="data-source-segment"
                role="group"
                aria-label="Choose table catalog or SQL query"
              >
              <button
                type="button"
                class="segment-btn"
                :class="{ active: dataSourceMode === 'sql' }"
                :aria-pressed="dataSourceMode === 'sql' ? 'true' : 'false'"
                :disabled="!reportConfigSqlEnabled"
                :title="reportConfigSqlEnabled ? 'Use a custom SQL SELECT' : 'SQL query mode is not enabled for your account'"
                @click="setDataSourceMode('sql')"
              >
                <span class="segment-btn__icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 5h16v2H4V5zm0 6h10v2H4v-2zm0 6h16v2H4v-2z" fill="currentColor" opacity=".85"/>
                    <path d="M18 11l2 2-2 2v-4z" fill="currentColor"/>
                  </svg>
                </span>
                <span class="segment-btn__text">SQL</span>
              </button>
              <button
                type="button"
                class="segment-btn"
                :class="{ active: dataSourceMode === 'table' }"
                :aria-pressed="dataSourceMode === 'table' ? 'true' : 'false'"
                @click="setDataSourceMode('table')"
              >
                <span class="segment-btn__icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16v3H4V4zm0 6h16v3H4v-3zm0 6h10v3H4v-3z" fill="currentColor" opacity=".75"/>
                    <path d="M16 16h4v7h-4v-7z" fill="currentColor"/>
                  </svg>
                </span>
                <span class="segment-btn__text">Table</span>
              </button>
              </div>

              <button
                v-if="isTableDataSource && catalogSidebarCollapsed"
                type="button"
                style="font-size: 12px; font-weight: 600; border-radius: 8px;"
                class="catalog-restore-btn mt-2"
                aria-label="Show catalog panel"
                title="Show catalog"
                @click="expandCatalogSidebar"
              >
                <span class="catalog-restore-btn__icon" aria-hidden="true">▸</span>
                <span class="catalog-restore-btn__label">Catalog</span>
              </button>
            </div>

            <!-- <div class="data-source-toolbar__center">
              <div
                class="data-source-segment"
                role="group"
                aria-label="Choose table catalog or SQL query"
              >
              <button
                type="button"
                class="segment-btn"
                :class="{ active: dataSourceMode === 'sql' }"
                :aria-pressed="dataSourceMode === 'sql' ? 'true' : 'false'"
                :disabled="!reportConfigSqlEnabled"
                :title="reportConfigSqlEnabled ? 'Use a custom SQL SELECT' : 'SQL query mode is not enabled for your account'"
                @click="setDataSourceMode('sql')"
              >
                <span class="segment-btn__icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 5h16v2H4V5zm0 6h10v2H4v-2zm0 6h16v2H4v-2z" fill="currentColor" opacity=".85"/>
                    <path d="M18 11l2 2-2 2v-4z" fill="currentColor"/>
                  </svg>
                </span>
                <span class="segment-btn__text">SQL</span>
              </button>
              <button
                type="button"
                class="segment-btn"
                :class="{ active: dataSourceMode === 'table' }"
                :aria-pressed="dataSourceMode === 'table' ? 'true' : 'false'"
                @click="setDataSourceMode('table')"
              >
                <span class="segment-btn__icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16v3H4V4zm0 6h16v3H4v-3zm0 6h10v3H4v-3z" fill="currentColor" opacity=".75"/>
                    <path d="M16 16h4v7h-4v-7z" fill="currentColor"/>
                  </svg>
                </span>
                <span class="segment-btn__text">Table</span>
              </button>
              </div>
            </div> -->
            <div class="data-source-toolbar__hint">
              <b-button
                  class="btn-generate-preview preview-section-heading__action btn-sm px-2"
                  variant="info"
                  :disabled="!isPreviewEnabled"
                  @click="generatePreview"
                >
                  Preview
                </b-button>
            </div>
          </div>
          <div
            v-if="previewFetchErrorVisible"
            class="alert alert-danger py-2 px-3 mt-2 mb-0 small preview-fetch-error preview-fetch-error--banner fw-copyable-text"
            role="alert"
            tabindex="0"
            @keydown="onCopyableRegionKeydown"
          >
            <div>{{ previewError }}</div>
            <template v-if="previewErrorQuery && isTableDataSource">
              <div class="text-muted mt-2 mb-1">Query that caused the error:</div>
              <pre class="preview-fetch-error-query mb-0">{{ previewErrorQuery }}</pre>
            </template>
          </div>
        </section>

        <b-row
          class="preview-setup-main"
          :class="{
            'preview-setup-main--catalog-hidden': isTableDataSource && catalogSidebarCollapsed,
            'preview-setup-main--table-catalog': isTableDataSource && !catalogSidebarCollapsed,
          }"
        >
          <div
            v-if="isTableDataSource && !catalogSidebarCollapsed"
            class="mb-2 mb-lg-0 preview-catalog-col"
            :class="{ 'preview-catalog-col--resizing': catalogPanelResizing }"
            :style="catalogColInlineStyle"
          >
            <div
              class="catalog-tree card border-0 shadow-sm"
              :class="{ 'catalog-tree--collapsed': !catalogPanelExpanded }"
            >
              <div class="catalog-tree__header">
                <span class="catalog-tree__title">Catalog</span>
                <b-form-input
                  v-model="catalogSearchQuery"
                  type="search"
                  size="sm"
                  class="catalog-tree__search"
                  placeholder="Search schemas or tables…"
                  aria-label="Filter catalog by schema or table name"
                  @input="applyCatalogSearchTreeExpansion"
                />
                <div class="catalog-tree__header-actions">
                  <button
                    type="button"
                    class="catalog-tree__action-btn"
                    :aria-label="catalogPanelExpanded ? 'Collapse catalog tree' : 'Expand catalog tree'"
                    :title="catalogPanelExpanded ? 'Collapse catalog' : 'Expand catalog'"
                    @click="toggleCatalogPanelExpanded"
                  >
                    <span
                      class="catalog-tree__action-icon catalog-tree__action-icon--vertical"
                      :class="{ 'is-collapsed': !catalogPanelExpanded }"
                      aria-hidden="true"
                    >▾</span>
                  </button>
                  <button
                    type="button"
                    class="catalog-tree__action-btn"
                    aria-label="Hide catalog panel"
                    title="Hide catalog to use full width"
                    @click="collapseCatalogSidebar"
                  >
                    <span class="catalog-tree__action-icon catalog-tree__action-icon--hide" aria-hidden="true">◂</span>
                  </button>
                </div>
              </div>
              <div v-show="catalogPanelExpanded" class="catalog-tree__body">
                <p
                  v-if="catalogSearchQuery.trim() && !filteredCatalogSchemas.length"
                  class="catalog-tree__empty text-muted mb-0 px-2 py-2 small"
                >
                  No schemas or tables match your search.
                </p>
                <div
                  v-for="schema in filteredCatalogSchemas"
                  :key="'sch-' + schema"
                  class="catalog-tree__schema"
                >
                  <div class="catalog-tree__row catalog-tree__row--schema">
                    <button
                      type="button"
                      class="catalog-tree__toggle-btn"
                      :aria-label="(treeExpandedSchemas[schema] ? 'Collapse' : 'Expand') + ' schema ' + schema"
                      @click="toggleTreeSchema(schema)"
                    >
                      <span
                        class="catalog-tree__chevron"
                        :class="{ 'is-open': treeExpandedSchemas[schema] }"
                        aria-hidden="true"
                        >▸</span
                      >
                      <span class="catalog-tree__ico catalog-tree__ico--schema" aria-hidden="true" />
                    </button>
                    <span
                      class="catalog-tree__label catalog-tree__copyable text-truncate"
                      :title="schema"
                      >{{ schema }}</span
                    >
                  </div>
                  <div v-show="treeExpandedSchemas[schema]" class="catalog-tree__nested">
                    <div
                      v-for="table in filteredCatalogTables(schema)"
                      :key="schema + '.' + table"
                      class="catalog-tree__table-block"
                    >
                      <div class="catalog-tree__table-head">
                        <button
                          type="button"
                          class="catalog-tree__chevron-btn"
                          :aria-label="(treeExpandedTables[treeTableKey(schema, table)] ? 'Collapse' : 'Expand') + ' columns for ' + table"
                          @click.stop="toggleTreeTable(schema, table)"
                        >
                          <span
                            class="catalog-tree__chevron catalog-tree__chevron--sm"
                            :class="{ 'is-open': treeExpandedTables[treeTableKey(schema, table)] }"
                            >▸</span
                          >
                        </button>
                        <div
                          class="catalog-tree__row catalog-tree__row--table flex-grow-1"
                          :class="{ 'is-active': selectedSchema === schema && selectedTableName === table }"
                          @click="selectCatalogTable(schema, table)"
                        >
                          <button
                            type="button"
                            class="catalog-tree__toggle-btn catalog-tree__toggle-btn--table"
                            :aria-label="'Select table ' + table"
                            @click.stop="selectCatalogTable(schema, table)"
                          >
                            <span class="catalog-tree__ico catalog-tree__ico--table" aria-hidden="true" />
                          </button>
                          <span
                            class="catalog-tree__label catalog-tree__copyable text-truncate"
                            :title="table"
                            >{{ table }}</span
                          >
                        </div>
                      </div>
                      <div
                        v-show="treeExpandedTables[treeTableKey(schema, table)]"
                        class="catalog-tree__columns"
                      >
                        <div
                          v-for="(col, colIdx) in catalogColumns(schema, table)"
                          :key="treeTableKey(schema, table) + '-col-' + colIdx"
                          class="catalog-tree__col"
                          :title="col.dataType"
                        >
                          <span
                            class="catalog-tree__type-dot"
                            :class="'catalog-tree__type-dot--' + columnTypeKind(col.dataType)"
                            aria-hidden="true"
                          />
                          <span class="catalog-tree__col-name catalog-tree__copyable text-truncate">{{ col.name }}</span>
                          <!-- <span class="catalog-tree__col-type text-truncate">{{ shortTypeLabel(col.dataType) }}</span> -->
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="catalog-resize-handle"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize catalog panel"
              title="Drag to resize catalog"
              @mousedown.prevent="startCatalogPanelResize"
            />
          </div>

          <div class="preview-main-col">
            <b-row v-if="!isTableDataSource" class="preview-sql-row mb-2">
              <b-col v-if="vizFunction !== 'tableBarChart'" md="6" class="preview-col-wrap">
                <b-form-group
                  label="DataSQL (reference)"
                  label-class="small text-muted"
                  label-align="left"
                  class="sql-form-group"
                >
                  <b-form-textarea
                    :value="cleanedDataSql"
                    disabled
                    rows="3"
                    max-rows="12"
                    auto-grow
                    :no-resize="false"
                    class="sql-textarea"
                  />
                </b-form-group>
              </b-col>
              <b-col :md="vizFunction !== 'tableBarChart' ? 6 : 12" class="preview-col-wrap">
                <b-form-group
                  :label="vizFunction !== 'tableBarChart' ? 'DataSQL' : ''"
                  label-class="small text-muted"
                  label-align="left"
                  class="sql-form-group"
                >
                  <b-form-textarea
                    v-model="dataSqlId"
                    rows="3"
                    max-rows="12"
                    auto-grow
                    :no-resize="false"
                    class="sql-textarea"
                    placeholder="SELECT …"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <section
              v-if="previewChartSectionVisible"
              class="preview-workflow-section preview-workflow-section--charts mb-2"
              aria-labelledby="preview-step-charts"
            >
              <div
                class="preview-charts preview-charts--panel mt-2"
                :class="{ 'preview-charts--single-preview': vizFunction === 'tableBarChart' }"
              >
                <template v-if="vizFunction !== 'tableBarChart'">
                  <div class="chart-panel chart-panel--existing">
                    <div class="chart-panel__toolbar">
                      <span class="chart-panel__title">Existing chart</span>
                    </div>
                    <div class="chart-panel__surface">
                      <div :id="id" ref="r1c1" class="scrollmenu chart-panel__canvas"></div>
                    </div>
                  </div>

                  <div class="chart-panel chart-panel--preview">
                    <div class="chart-panel__toolbar">
                      <span class="chart-panel__title">Preview chart</span>
                    </div>
                    <div class="chart-panel__surface">
                      <div
                        v-if="!previewChartsVisible && !previewLoading"
                        class="preview-chart-placeholder text-muted"
                      >
                        <div class="preview-chart-placeholder__icon" aria-hidden="true">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 19V5h16v14H4zm2-2h12V7H6v10z" stroke="currentColor" stroke-width="1.2" fill="none" opacity=".35"/>
                            <path d="M8 15l3-3 2.5 2.5L18 10v5H8v0z" fill="currentColor" opacity=".25"/>
                          </svg>
                        </div>
                        <p class="mb-0 small">
                          Configure the data source above, then click <strong>Preview</strong> to render here.
                        </p>
                      </div>
                      <template v-else>
                        <!-- <div
                          v-if="previewError && !previewLoading"
                          class="alert alert-danger py-2 px-3 mb-2 small preview-fetch-error"
                          role="alert"
                        >
                          <div>{{ previewError }}</div>
                          <template v-if="previewErrorQuery">
                            <div class="text-muted mt-2 mb-1">Query that caused the error:</div>
                            <pre class="preview-fetch-error-query mb-0">{{ previewErrorQuery }}</pre>
                          </template>
                        </div> -->
                        <div v-if="previewLoading" class="chart-panel__loading text-muted small">
                          <b-spinner small variant="primary" class="mr-2 align-middle" />
                          Loading preview…
                        </div>
                        <div
                          v-show="!previewLoading"
                          :id="id + '-p'"
                          ref="r1c2"
                          class="scrollmenu chart-panel__canvas"
                        ></div>
                      </template>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="chart-panel chart-panel--preview chart-panel--table-bar">
                    <!-- <div class="chart-panel__toolbar">
                      <span class="chart-panel__title">Preview chart</span>
                      <span v-if="previewLoading" class="chart-panel__badge text-muted small">
                        <b-spinner small variant="primary" class="mr-1 align-middle" />
                        Loading…
                      </span>
                    </div> -->
                    <div class="chart-panel__surface">
                      <div v-if="previewLoading" class="chart-panel__loading text-muted small">
                        <b-spinner small variant="primary" class="mr-2 align-middle" />
                        Loading preview…
                      </div>
                      <div
                        v-show="!previewLoading"
                        :id="id + '-p'"
                        ref="r1c2"
                        class="scrollmenu chart-panel__canvas"
                      ></div>
                    </div>
                  </div>
                </template>
              </div>
            </section>

            <section
              v-if="previewMappingSectionVisible"
              class="preview-workflow-section preview-workflow-section--mapping mb-2"
              aria-labelledby="preview-step-mapping"
            >
              <!-- <div class="preview-section-heading">
                <span class="setup-step-badge setup-step-badge--muted" aria-hidden="true">3</span>
                <div class="flex-grow-1 min-w-0">
                  <div id="preview-step-mapping" class="preview-section-heading__title">
                    Variable mapping
                  </div>
                </div>
              </div> -->

              <b-row class="preview-mapping-row pb-1 mt-2">
              <b-col v-if="vizFunction !== 'tableBarChart'" md="6" class="preview-col-wrap">
                <div class="mapping-table-wrap">
                  <b-table-simple
                    v-if="vizParams && Object.keys(vizParams).length"
                    bordered
                    small
                    responsive
                    class="mapping-table"
                  >
                    <b-thead head-variant="light">
                      <b-tr>
                        <b-th>Required</b-th>
                        <b-th>Column</b-th>
                      </b-tr>
                    </b-thead>
                    <b-tbody>
                      <b-tr
                        v-for="row in existingMappingRows"
                        :key="'existing-' + row.axisLabel"
                      >
                        <b-td>{{ row.axisLabel }}</b-td>
                        <b-td
                          ><code>{{ row.expression }}</code></b-td
                        >
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                  <p v-else class="small text-muted mb-0">No saved axis mapping for this viz.</p>
                </div>
              </b-col>
              <b-col :md="vizFunction !== 'tableBarChart' ? 6 : 12" class="preview-col-wrap">
                <div
                  class="mapping-table-wrap"
                  :class="{
                    'mapping-table-wrap--table-bar':
                      vizFunction === 'tableBarChart' || isTableDataSource,
                  }"
                >
                  <b-table-simple
                    v-if="!isTableDataSource && vizFunction !== 'tableBarChart'"
                    bordered
                    small
                    responsive
                    class="mapping-table"
                  >
                    <b-thead head-variant="light">
                      <b-tr>
                        <b-th>Required</b-th>
                        <b-th>SQL expression</b-th>
                        <b-th>Display name</b-th>
                        <b-th class="text-center">Filter</b-th>
                      </b-tr>
                    </b-thead>
                    <b-tbody>
                      <b-tr
                        v-for="row in previewMappingRows"
                        :key="'preview-' + row.axisLabel"
                      >
                        <b-td class="align-middle">{{ row.axisLabel }}</b-td>
                        <b-td class="align-middle">
                          <input
                            v-model="row.expression"
                            class="inline-mapping-input"
                            placeholder="Enter column or expression"
                            @input="syncPreviewRowDisplayNameFromExpression(row)"
                          />
                        </b-td>
                        <b-td class="align-middle">
                          <input
                            v-model="row.displayName"
                            class="inline-mapping-input"
                            placeholder="Business / header name"
                            :title="'Listbox label for ' + (row.expression || row.axisLabel)"
                          />
                        </b-td>
                        <b-td class="text-center align-middle">
                          <b-form-checkbox v-model="row.filterable" size="sm" class="mb-0" />
                        </b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>

                  <VariableMappingPanel
                    v-else
                    :mode="isTableDataSource ? 'table' : 'sql'"
                    :table-schema="selectedSchema"
                    :table-name="selectedTableName"
                    :rows="adminVariableMappingRows"
                    :header-map="defaultChartData.reportHeaderColumnDp || {}"
                    :search-query.sync="mappingTableSearchQuery"
                    context="admin"
                    layout="admin"
                    :show-graph-column="vizFunction === 'tableBarChart'"
                    :show-mode-badge="false"
                    @row-change="onAdminVariableMappingRowChange"
                    @set-all-include="setAllTableColumnInclude"
                  />
                </div>
              </b-col>
            </b-row>

            </section>
          </div>
        </b-row>
        <b-row v-if="vizFunction === 'tableBarChart'">
          <b-col md="4">
            <ValidationProvider
              name="Menu Name"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Menu Name" label-align="left">
                <div class="menu-name-prefix-input">
                  <span
                    class="menu-name-prefix-input__prefix"
                    aria-hidden="true"
                  >{{ menuNamePrefix }}</span>
                  <input
                    type="text"
                    class="form-control menu-name-prefix-input__field menu-config-text-input"
                    v-model="menuNameInput"
                    :placeholder="menuNamePlaceholder"
                  />
                </div>
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col md="4">
            <ValidationProvider
              name="Screen Title"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Screen Title" label-align="left">
                <b-form-input
                  v-model="screenName"
                  class="menu-config-text-input"
                  placeholder="Enter the Screen Title"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>
        <div v-if="vizFunction === 'tableBarChart'" class="d-flex justify-content-end mt-1">
          <b-button style="font-size: 12px; font-weight: 600; border-radius: 8px;" variant="secondary" @click="resetForm" class="mr-2">
            Reset
          </b-button>
          <b-button style="font-size: 12px; font-weight: 600; border-radius: 8px;" variant="primary" @click="submitForm" :disabled="menuSaving">
            <b-spinner v-if="menuSaving" small class="mr-1" />
            {{ menuSaving ? "Saving…" : "Submit" }}
          </b-button>
        </div>
      </b-card>

      <!-- Layer 1 -->
      <b-card v-if="vizFunction !== 'tableBarChart'" class="mb-3 shadow-sm" style="padding: 8px 18px">
        <b-card-title class="mb-3">Menu Configuration</b-card-title>
        <b-row class="mb-3 border-bottom pb-3">
          <!-- <b-col md="6">
            <b-form-group label="Parent Menu Name" label-align="left">
              <b-form-input :value="defaultParentMenuId" disabled />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6"> -->
            <!-- <ValidationProvider
              name="Parent Menu Name"
              rules="required"
              v-slot="{ errors }"
            > -->
              <!-- <b-form-group
                label="Parent Menu Name"
                label-align="left"
                class="position-relative"
              > -->
                <!-- <div class="d-flex align-items-center">
                  <multiselect
                    v-model="parentMenuId"
                    :options="parentMenuIdOptions"
                    :multiple="false"
                    select-label="Select"
                    deselect-label="Unselect"
                    :limit="1"
                    label="name"
                    track-by="id"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    class="flex-grow-1"
                  ></multiselect> -->

                  <!-- Add icon button with tooltip -->
                  <!-- <b-button
                    variant="link"
                    class="ml-2 p-0 text-primary"
                    v-b-tooltip.hover="'Add new Parent Menu Id'"
                    @click="showParentMenuModal = true"
                  >
                    <b-icon-plus-circle font-scale="1.5"></b-icon-plus-circle>
                  </b-button> -->
                <!-- </div> -->
                <!-- <span class="text-danger">{{ errors[0] }}</span> -->
              <!-- </b-form-group> -->
            <!-- </ValidationProvider> -->

            <!-- Modal for adding new parent menu -->
            <!-- <b-modal
              v-model="showParentMenuModal"
              title="Add New Parent Menu"
              @ok="addNewParentMenu"
            >
              <b-form-group label="Parent Menu Name">
                <b-form-input
                  v-model="newParentMenuName"
                  placeholder="Enter parent menu name"
                ></b-form-input>
              </b-form-group>
              <b-form-group label="Parent Menu Id">
                <b-form-input
                  v-model="newParentMenuId"
                  placeholder="Enter parent menu id"
                ></b-form-input>
              </b-form-group>

              <template #modal-footer="{ ok, cancel }">
                <b-button variant="secondary" @click="cancel">Cancel</b-button>
                <b-button variant="primary" @click="ok">OK</b-button>
              </template>
            </b-modal> -->
          <!-- </b-col> -->
          <!-- <b-col md="6">
            <b-form-group label="Menu Id" label-align="left">
              <b-form-input :value="fetchDefaultvalues[0].menuId" disabled />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6">
            <ValidationProvider
              name="Menu Id"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Menu Id" label-align="left">
                <b-form-input
                  v-model="menuId"
                  :placeholder="menuIdPlaceholder"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col> -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Menu Name" label-align="left">
              <b-form-input :value="defaultMenuName" disabled />
            </b-form-group>
          </b-col>
          <b-col :md="vizFunction !== 'tableBarChart' ? 6 : 12">
            <ValidationProvider
              name="Menu Name"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Menu Name" label-align="left">
                <div class="menu-name-prefix-input">
                  <span
                    class="menu-name-prefix-input__prefix"
                    aria-hidden="true"
                  >{{ menuNamePrefix }}</span>
                  <input
                    type="text"
                    class="form-control menu-name-prefix-input__field menu-config-text-input"
                    v-model="menuNameInput"
                    :placeholder="menuNamePlaceholder"
                  />
                </div>
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>
        <b-row class="mb-3 border-bottom pb-3">
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Screen Title" label-align="left">
              <b-form-input :value="defaultMenuName" disabled />
            </b-form-group>
          </b-col>
          <b-col :md="vizFunction !== 'tableBarChart' ? 6 : 12">
            <ValidationProvider
              name="Screen Title"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Screen Title" label-align="left">
                <b-form-input
                  v-model="screenName"
                  class="menu-config-text-input"
                  placeholder="Enter the Screen Title"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Filter SQL Id" label-align="left">-->
          <!--            <b-form-input :value="fetchDefaultvalues[0].screenletListbox1_sqlId" disabled />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Filter SQL Id" label-align="left">-->
          <!--            <b-form-input v-model="filterSqlId" placeholder="Enter the Filter SQL"/>-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Filter Syspk" label-align="left">-->
          <!--            <b-form-input :value="JSON.stringify(defaultFilterSyspk)" disabled />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Filter Syspk" label-align="left">-->
          <!--            <b-form-input v-model="filterSyspk" placeholder="Enter the Filter Syspk"/>-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Data SQL Id" label-align="left">-->
          <!--            <b-form-input :value="fetchDefaultvalues[0].datasqlId" disabled />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <ValidationProvider name="Data SQL Id" rules="required" v-slot="{ errors }">-->
          <!--            <b-form-group label="Data SQL Id" label-align="left">-->
          <!--              <b-form-input v-model="dataSqlId" placeholder="Enter the Data SQL" />-->
          <!--              <span class="text-danger">{{ errors[0] }}</span>-->
          <!--            </b-form-group>-->
          <!--          </ValidationProvider>-->
          <!--        </b-col>-->
          <!-- <b-col md="6">
            <b-form-group label="Viz Type" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].viztypeName"
                disabled
              />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6">
            <ValidationProvider
              name="Viz Type"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Viz Type" label-align="left">
                <multiselect
                  v-model="vizTypeName"
                  :options="vizTypeOptions"
                  :multiple="false"
                  select-label="Select"
                  deselect-label="Unselect"
                  :limit="1"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                >
                </multiselect>
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col> -->
          <!--        <b-col sm="4">-->
          <!--          <b-form-group label="Viz Size ID">-->
          <!--            <b-form-input :value="fetchDefaultvalues[0].vizletSizeId" disabled />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col sm="4">-->
          <!--          <b-form-group label="Viz Size ID">-->
          <!--            <b-form-select v-model="vizSizeId" :options="vizSizeOptions" required />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
        </b-row>
        <b-row>
          <!-- X-Axis Name -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Name" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].x1_axisDisplayName"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Name" label-align="left">
              <b-form-input
                v-model="x_axisName"
                placeholder="Enter the XAxis Name"
              />
            </b-form-group>
          </b-col>

          <!-- Y-Axis Name -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Name" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].y1_axisDisplayName"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Name" label-align="left">
              <b-form-input
                v-model="y_axisName"
                placeholder="Enter the YAxis Name"
              />
            </b-form-group>
          </b-col>

          <!-- Viz Title Name -->
          <!-- <b-col md="6">
            <b-form-group label="Viz Title" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].vizletTitleDpName"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <ValidationProvider
              name="Viz Title"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Viz Title" label-align="left">
                <b-form-input
                  v-model="vizTitleName"
                  required
                  placeholder="Enter the Viz Title"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col> -->

          <!-- X-Axis Tooltip -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Tooltip" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].x1_metricTooltip"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Tooltip" label-align="left">
              <b-form-input
                v-model="x_axisTooltip"
                placeholder="Enter the XAxis Tooltip"
              />
            </b-form-group>
          </b-col>

          <!-- Y-Axis Tooltip -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Tooltip" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].y1_metricTooltip"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Tooltip" label-align="left">
              <b-form-input
                v-model="y_axisTooltip"
                placeholder="Enter the YAxis Tooltip"
              />
            </b-form-group>
          </b-col>

          <!-- X-Axis Data Name -->
          <!-- <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Data Name" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].x1_dataFieldName"
                disabled
              />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Data Name" label-align="left">
              <b-form-input
                v-model="x_axisDataName"
                placeholder="Enter the XAxis Data Name"
              />
            </b-form-group>
          </b-col> -->

          <!-- Y-Axis Data Name -->
          <!-- <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Data Name" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].y1_dataFieldName"
                disabled
              />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Data Name" label-align="left">
              <b-form-input
                v-model="y_axisDataName"
                placeholder="Enter the YAxis Data Name"
              />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6" v-if="vizFunction === 'tableBarChart'">
            <b-form-group label="Table Headers" label-align="left">
              <b-form-textarea
                :value="
                  JSON.stringify(fetchDefaultvalues[0].reportHeaderColumnDp)
                "
                disabled
              />
            </b-form-group>
          </b-col> -->
        </b-row>
        <div v-if="vizFunction !== 'tableBarChart'" class="d-flex justify-content-end mt-1">
          <b-button style="font-size: 12px; font-weight: 600; border-radius: 6px;" variant="secondary" @click="resetForm" class="mr-2">
            Reset
          </b-button>
          <b-button style="font-size: 12px; font-weight: 600; border-radius: 6px;" variant="primary" @click="submitForm" :disabled="menuSaving">
            <b-spinner v-if="menuSaving" small class="mr-1" />
            {{ menuSaving ? "Saving…" : "Submit" }}
          </b-button>
        </div>
      </b-card>
    </div>
  </ValidationObserver>
</template>

<script>
import axios from "axios";
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
// import Multiselect from "vue-multiselect/src/Multiselect.vue";
// import { BIconPlusCircle } from "bootstrap-vue";
import { apiAbort } from "@/common/apiAbort";
import {
  classifyPreviewOrFetchError,
  extractFailedFetchQueryFromError,
} from "@/common/sqlDataSqlAlias";
import {
  normalizeDataSourceMode,
  dataSourceModeToVizParamValue,
  fetchTableCatalogCached,
  resolveSqlMappingRowLabel,
  resolveSqlMappingRowGraph,
} from "@/common/columnMappingCore";
import { buildVizletSqlModeColumnLayout } from "@/common/columnMappingTableSql";
import {
  acquireSelectionLock,
  releaseSelectionLock,
  forceReleaseSelectionLock,
  onCopyableRegionKeydown,
} from "@/common/selectionLock";
import VariableMappingPanel from "@/components/variable-mapping/VariableMappingPanel.vue";
import { FwVizChartInfo } from "@/models";
import { Parser as SqlParser } from "node-sql-parser";

const sqlParser = new SqlParser();

const TABLE_MODE_AGG_OPTIONS_METRIC = [
  { value: "", text: "None (column)" },
  { value: "SUM", text: "SUM" },
  { value: "AVG", text: "AVG" },
  { value: "COUNT", text: "COUNT" },
  { value: "MIN", text: "MIN" },
  { value: "MAX", text: "MAX" },
];

const TABLE_MODE_AGG_OPTIONS_DATE = [
  { value: "", text: "None (column)" },
  { value: "YEAR", text: "Year" },
  { value: "QUARTER", text: "Quarter" },
  { value: "MONTH", text: "Month" },
  { value: "YYYYMM", text: "YYYYMM" },
];

const TABLE_MODE_AGG_OPTION_COUNT_DISTINCT = {
  value: "COUNT_DISTINCT",
  text: "COUNT (distinct)",
};

const TABLE_MODE_AGG_OPTIONS_DIMENSION = [
  { value: "", text: "—" },
  { value: "COUNT", text: "COUNT" },
  TABLE_MODE_AGG_OPTION_COUNT_DISTINCT,
];

function appendCountDistinctOption(options) {
  if (!options.some((o) => o.value === TABLE_MODE_AGG_OPTION_COUNT_DISTINCT.value)) {
    return [...options, TABLE_MODE_AGG_OPTION_COUNT_DISTINCT];
  }
  return options;
}

// Add required rule
extend("required", {
  ...required,
  message: "{_field_} is required",
});

export default {
  name: "menuConfig",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    selectedOptions: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    // Multiselect,
    ValidationProvider,
    ValidationObserver,
    VariableMappingPanel,
    // BIconPlusCircle,
  },
  watch: {
    filters: {
      handler() {
        // Only re-init and re-render when filters prop changes
        this.initMenuDefaults(this.activeMenuId);
        this.renderExistingChart();
        this.invalidatePreview();
      },
      deep: true,
    },
    vizParams: {
      handler() {
        this.initPreviewMapping();
        this.invalidatePreview();
      },
      deep: true,
      immediate: true,
    },
    dataSqlId: {
      handler(sql) {
        if (this.isTableDataSource) return;
        if (this.vizFunction === "tableBarChart") {
          this.syncTableBarPreviewRowsFromSql(sql);
        }
        this.invalidatePreview();
      },
    },
    reportConfigSqlEnabled: {
      handler(enabled) {
        if (!enabled && this.dataSourceMode === "sql") {
          this.setDataSourceMode("table");
        }
      },
    },
    dataSourceMode(mode) {
      this.invalidatePreview();
      if (mode !== "table") return;
      this.fetchTableCatalog().then(() => {
        this.$nextTick(() => {
          this.initTableModeFromCatalog();
        });
      });
    },
    tableModeColumnRows: {
      handler() {
        if (!this.isTableDataSource) return;
        this.syncGeneratedDataSqlFromTable();
      },
      deep: true,
    },
  },
  created() {
    console.log("selectedOptions", this.selectedOptions);
    this.initMenuDefaults(this.activeMenuId);
    this.applyDefaultDataSourceMode();
  },
  beforeDestroy() {
    this.stopCatalogPanelResize();
    forceReleaseSelectionLock();
  },
  data: () => ({
    id: `VIZ-${new Date().getTime()}`,
    // Layer 1
    loadMenu: true,
    menuSaving: false,
    defaultParentMenuId: null,
    parentMenuId: null,
    parentMenuIdOptions: [],
    radioValue: "1",
    dimension: "2D",
    dimensionOptions: [
      { text: "2D", value: "2D" },
      { text: "3D", value: "3D" },
    ],

    // Layer 2
    menuId: "",
    menuIdPlaceholder: "Enter Menu Id",
    defaultMenuName: "",
    menuNamePrefix: "My Dashboard/",
    menuName: "",
    menuNamePlaceholder: "Enter Menu Name",

    // Layer 3
    screenName: "",
    screenNamePlaceholder: "Enter Screen Name",
    filterSqlId: "",
    filterSqlIdPlaceholder: "Enter Filter SQL Id",
    defaultFilterSyspk: "",
    filterSyspk: "",
    filterSyspkPlaceholder: "Enter Filter Syspk",

    // Layer 4
    dataSqlId: "",
    dataSqlIdPlaceholder: "Enter Data SQL Id",
    vizTypeName: "",
    vizTypeOptions: [
      /* viz type options */
    ],
    vizSizeId: "VS72",
    vizSizeOptions: [
      /* viz size options */
    ],

    // Layer 5
    x_axisName: "",
    xAxisNamePlaceholder: "Enter X-Axis Name",
    y_axisName: "",
    yAxisNamePlaceholder: "Enter Y-Axis Name",
    vizTitleNamePlaceholder: "Enter Viz Title Name",
    vizTitleName: "",
    x_axisTooltip: "",
    xAxisTooltipPlaceholder: "Enter X-Axis Tooltip",
    y_axisTooltip: "",
    yAxisTooltipPlaceholder: "Enter Y-Axis Tooltip",
    x_axisDataName: "",
    xAxisDataNamePlaceholder: "Enter X-Axis Data Name",
    y_axisDataName: "",
    yAxisDataNamePlaceholder: "Enter Y-Axis Data Name",
    tableHeaders: [],
    nextFieldId: 0,
    showParentMenuModal: false,
    newParentMenuName: "",
    newParentMenuId: "",
    previewLoading: false,
    previewChartsVisible: false,
    previewError: "",
    previewErrorQuery: "",
    treeExpandedSchemas: {},
    treeExpandedTables: {},
    previewData: [],
    previewConfig: {},
    dataSourceMode: "sql",
    catalogSidebarCollapsed: false,
    catalogPanelExpanded: true,
    catalogSearchQuery: "",
    catalogPanelWidth: 300,
    catalogPanelResizing: false,
    selectedSchema: "",
    selectedTableName: "",
    tableModeColumnRows: [],
    mappingTableSearchQuery: "",
    tableModeDisplayTypeOptions: [
      { value: "Dimension", text: "Dimension" },
      { value: "Metric", text: "Metric" },
      { value: "Date", text: "Date" },
      { value: "Identifier", text: "Identifier" },
    ],
    vizFunction: "",
    previewMappingRows: [],
    previewTableBarRows: [],
    tableBarTypeOptions: [
      { value: "Dimension", text: "Dimension" },
      { value: "Metric", text: "Metric" },
    ],
    tableBarGraphOptions: [
      { value: "no", text: "No" },
      { value: "yes", text: "Yes" },
    ],
    /** Populated from GET /api/table-catalog (schema → tables → [{ name, dataType }]). */
    tableCatalog: {},
    tableCatalogFetched: false,
    vizParamJson: {},
    defaultChartData: {
      datasql:"",
      width: 800,
      height: 300,
      axisFontSize: 8,
      axisFontColor: "black",
      axisLabelFontSize: 6,
      axisLabelFontColor: "black",
      x1AxisDisplayName: "",
      x2AxisDisplayName: "",
      y1AxisDisplayName: "",
      y2AxisDisplayName: "",
      x1DataFieldName: "",
      x2DataFieldName: "",
      y1DataFieldName: "",
      y2DataFieldName: "",
      reportHeaderColumnDp: "",
      x1D3ScaleType: "timeScale",
      x2D3ScaleType: "timeScale",
      y1D3ScaleType: "linearScale",
      y2D3ScaleType: "linearScale",
      reportHeaderColumnDpWordwrap: {
        wordwrap: { "table heading": "0", "table data": "0" },
      },
      tabReportJson: {},
      x1AxisLabelCharLimitsJson: {
        break: { value1: "0", value2: "/( |,|_|(?=[A-Z]))/" },
      },
      margin: {
        top: 10,
        right: 50,
        bottom: 50,
        left: 50,
      },
    },
  }),
  computed: {
    menuNameInput: {
      get() {
        return this.menuName;
      },
      set(value) {
        this.menuName = this.stripMenuNamePrefix(value);
      },
    },
    isTableDataSource() {
      return this.dataSourceMode === "table";
    },
    reportConfigSqlEnabled() {
      const params =
        this.$store.state.user && this.$store.state.user.userConfigParams;
      const raw = params && params.show_report_config_sql;
      const value = String(raw == null ? "Yes" : raw)
        .trim()
        .toLowerCase();
      if (value === "no" || value === "n" || value === "false") return false;
      return true;
    },
    defaultDataSourceMode() {
      return this.reportConfigSqlEnabled ? "sql" : "table";
    },
    allTableColumnsIncluded() {
      const rows = this.tableModeColumnRows || [];
      return rows.length > 0 && rows.every((r) => r && r.include);
    },
    tableColumnsIncludeIndeterminate() {
      const rows = (this.tableModeColumnRows || []).filter((r) => r);
      if (!rows.length) return false;
      const includedCount = rows.filter((r) => r.include).length;
      return includedCount > 0 && includedCount < rows.length;
    },
    resolvedTableCatalog() {
      const c = this.tableCatalog;
      return c && typeof c === "object" && !Array.isArray(c) ? c : {};
    },
    sortedCatalogSchemas() {
      return Object.keys(this.resolvedTableCatalog).sort();
    },
    catalogSearchNormalized() {
      return (this.catalogSearchQuery || "").trim().toLowerCase();
    },
    mappingTableSearchNormalized() {
      return (this.mappingTableSearchQuery || "").trim().toLowerCase();
    },
    filteredTableModeColumnRows() {
      const rows = this.tableModeColumnRows || [];
      const q = this.mappingTableSearchNormalized;
      if (!q) return rows;
      return rows.filter((row) => {
        const col = String(row.columnName || "").toLowerCase();
        const disp = String(row.displayName || "").toLowerCase();
        return col.includes(q) || disp.includes(q);
      });
    },
    filteredPreviewTableBarRows() {
      const rows = this.previewTableBarRows || [];
      const q = this.mappingTableSearchNormalized;
      if (!q) return rows;
      return rows.filter((row) => {
        const expr = String(row.expr || "").toLowerCase();
        const label = String(row.label || "").toLowerCase();
        const required = String(row.required || "").toLowerCase();
        return expr.includes(q) || label.includes(q) || required.includes(q);
      });
    },
    adminVariableMappingRows() {
      if (this.isTableDataSource) {
        return this.filteredTableModeColumnRows;
      }
      return this.filteredPreviewTableBarRows;
    },
    filteredCatalogSchemas() {
      const q = this.catalogSearchNormalized;
      const catalog = this.resolvedTableCatalog;
      const schemas = Object.keys(catalog).sort();
      if (!q) return schemas;
      return schemas.filter((schema) => {
        if (schema.toLowerCase().includes(q)) return true;
        const tables = catalog[schema] || {};
        return Object.keys(tables).some((t) => t.toLowerCase().includes(q));
      });
    },
    catalogColInlineStyle() {
      return {
        width: `${this.catalogPanelWidth}px`,
        flex: `0 0 ${this.catalogPanelWidth}px`,
        maxWidth: `${this.catalogPanelWidth}px`,
      };
    },
    hasDataSourceInput() {
      if (this.isTableDataSource) {
        return !!(this.selectedSchema && this.selectedTableName);
      }
      return !!(this.dataSqlId && this.dataSqlId.trim());
    },
    previewMappingSectionVisible() {
      if (!this.hasDataSourceInput) return false;
      if (this.vizFunction === "tableBarChart") return true;
      if (this.isTableDataSource) return true;
      return !!(this.vizParams && Object.keys(this.vizParams).length);
    },
    /** Hide preview chart on fetch failure; table bar mounts only after successful Preview. */
    previewChartSectionVisible() {
      if (this.previewError && !this.previewLoading) return false;
      if (this.vizFunction !== "tableBarChart") return true;
      return this.previewLoading || this.previewChartsVisible;
    },
    previewFetchErrorVisible() {
      return !!this.previewError && !this.previewLoading;
    },
    activeMenuId() {
      console.log("activeMenuId", this.filters);
      // When filters have real values, drive menuId from selectedOptions.menu_id
      const hasFilters = this.filters && Object.keys(this.filters).length > 0;
      console.log("selectedOptions", this.selectedOptions);

      if (hasFilters) {
        const sel = this.selectedOptions || {};
        const ids = sel.menu_id;
        if (Array.isArray(ids) && ids.length && ids[0]) {
          return ids[0];
        }
      }

      // Fallback default when no filters / menu selection available yet
      return "I11YTD";
    },
    vizParams() {
      const vizData = this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
      if (!vizData.length) return {};
      const allVizType = this.$store.state.vizType;
      const matched = allVizType.find(
        (t) => t.viztypeId === vizData[0].vizletId,
      );
      return matched?.vizParams || {};
    },
    fetchDefaultvalues() {
      const vizData = this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
      console.log(vizData);
      return vizData;
    },
    cleanedDataSql() {
      let rawSql = this.fetchDefaultvalues?.[0]?.datasql || "";
      if (!rawSql) return "";

      // Step 1: Remove fw placeholders
      rawSql = rawSql
        .replace(/\bfw_where_clause\b/gi, "")
        .replace(/\bfw_and_clause\b/gi, "")
        .replace(/\bfw_core_config_conn\b/gi, "");

      // Step 2: Extract SELECT part
      const selectMatch = rawSql.match(/select([\s\S]*?)from/i);
      if (!selectMatch) return rawSql.trim();

      const selectPart = selectMatch[1];
      const afterFrom = rawSql.substring(
        selectMatch.index + selectMatch[0].length - 4,
      );

      const columns = selectPart
        .replace(/\n/g, " ")
        .match(/(?:[^,(]|\([^)]*\))+/g);

      const aliasMap = {};
      const newColumns = [];

      columns.forEach((col) => {
        let column = col.trim();

        const aliasMatch = column.match(/\bas\s+([a-zA-Z0-9_]+)/i);

        if (aliasMatch) {
          const alias = aliasMatch[1];
          const original = column.replace(/\bas\s+[a-zA-Z0-9_]+/i, "").trim();

          aliasMap[alias] = original;
          newColumns.push(original);
        } else {
          newColumns.push(column);
        }
      });

      // Step 3: Replace alias usage in rest of query
      let updatedAfterFrom = afterFrom;

      Object.keys(aliasMap).forEach((alias) => {
        const regex = new RegExp(`\\b${alias}\\b`, "gi");
        updatedAfterFrom = updatedAfterFrom.replace(regex, aliasMap[alias]);
      });

      const finalSql = `select ${newColumns.join(", ")} ${updatedAfterFrom}`;

      return finalSql.replace(/\n\s*\n/g, "\n").trim();
    },
    parsedExistingSqlAliases() {
      const rawSql = this.fetchDefaultvalues?.[0]?.datasql || "";
      if (!rawSql) return {};
      return this.buildSqlAliasMapFromQuery(rawSql);
    },
    isPreviewEnabled() {
      if (this.isTableDataSource) {
        if (!this.selectedSchema || !this.selectedTableName) return false;
        const any =
          this.tableModeColumnRows &&
          this.tableModeColumnRows.some((r) => r && r.include);
        return !!any;
      }
      if (!this.dataSqlId || !this.dataSqlId.trim()) return false;
      if (this.vizFunction === "tableBarChart") {
        return this.previewTableBarRows.length > 0;
      }
      if (!this.previewMappingRows.length) return false;
      return this.previewMappingRows.every(
        (row) => row.expression && row.expression.trim(),
      );
    },
    existingMappingRows() {
      if (!this.vizParams || !Object.keys(this.vizParams).length) return [];

      const aliasMap = this.parsedExistingSqlAliases;

      return Object.entries(this.vizParams).map(([label, alias]) => ({
        axisLabel: label,
        alias,
        expression: aliasMap[alias] || "—",
        type:
          typeof alias === "string" && alias.toLowerCase().startsWith("metric")
            ? "Metric"
            : "Dimension",
      }));
    },
    tableBarHasGraph() {
      if (this.vizFunction !== "tableBarChart") return false;
      const headerMap =
        this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp || {};
      return Object.values(headerMap).some(
        (v) => typeof v === "string" && v.toLowerCase().startsWith("graph"),
      );
    },
    tableBarExistingMappingRows() {
      if (this.vizFunction !== "tableBarChart") return [];
      const headerMap =
        this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp || {};

      const entries = Object.entries(headerMap).filter(
        ([, value]) =>
          !(
            typeof value === "string" && value.toLowerCase().startsWith("graph")
          ),
      );

      const graphLabelSet = new Set();
      Object.entries(headerMap).forEach(([label, value]) => {
        if (
          typeof value === "string" &&
          value.toLowerCase().startsWith("graph")
        ) {
          const baseLabel = label.replace(/#+.*$/, "").replace(/#$/, "");
          graphLabelSet.add(baseLabel);
        }
      });

      let index = 1;
      return entries.map(([label, value]) => {
        const isMetric =
          typeof value === "string" && value.toLowerCase().startsWith("metric");
        const hasGraph = isMetric && graphLabelSet.has(label);
        const required = `Col${index++}`;
        return {
          required,
          column: label,
          field: value,
          type: isMetric ? "Metric" : "Dimension",
          graph: hasGraph ? "yes" : "no",
        };
      });
    },
  },
  mounted() {
    this.renderExistingChart();
  },
  methods: {
    stripMenuNamePrefix(value) {
      const str = String(value ?? "");
      if (!str) return "";
      if (str.startsWith(this.menuNamePrefix)) {
        return str.slice(this.menuNamePrefix.length);
      }
      return str;
    },
    initMenuDefaults(menuId) {
      console.log("initMenuDefaults", menuId);
      const uniqueParentMenuIdsMap = new Map();
      this.$store.state.menuMasterData.forEach((item) => {
        if (item.menuId !== null && item.menuId !== undefined) {
          uniqueParentMenuIdsMap.set(item.menuId, {
            id: item.menuId,
            name: item.menuName,
          });
        }
      });
      const allParentMenuIds = Array.from(uniqueParentMenuIdsMap.values());
      this.parentMenuIdOptions = allParentMenuIds;
      const parentId = this.$store.state.menuMasterFlat.find(
        (item) => item.menuId === menuId,
      ).parentMenuId;
      this.defaultParentMenuId = this.$store.state.menuMasterData.find(
        (item) => item.menuId === parentId,
      ).menuName;
      console.log("default", this.$store.state.menuMasterData);
      this.defaultMenuName = this.$store.state.menuMasterFlat.find(
        (item) => item.menuId === menuId,
      ).menuName;
      this.vizTypeOptions = this.$store.state.vizType
        .map((item) => item.viztypeName)
        .sort();
      this.defaultFilterSyspk = this.$store.state.fwConfigMenuScreenRender.find(
        (item) => item.menuId === menuId,
      ).screenletListboxSyspkArray;
      console.log(this.defaultFilterSyspk);
    },
    splitSelectColumns(selectPart) {
      const columns = [];
      let depth = 0;
      let current = "";
      let inSingle = false;
      let inDouble = false;

      for (let i = 0; i < selectPart.length; i++) {
        const char = selectPart[i];
        const prev = i > 0 ? selectPart[i - 1] : "";

        if (char === "'" && !inDouble && prev !== "\\") inSingle = !inSingle;
        if (char === '"' && !inSingle && prev !== "\\") inDouble = !inDouble;

        if (!inSingle && !inDouble) {
          if (char === "(") depth++;
          if (char === ")") depth--;
          if (char === "," && depth === 0) {
            columns.push(current.trim());
            current = "";
            continue;
          }
        }
        current += char;
      }
      if (current.trim()) columns.push(current.trim());

      return columns;
    },

    normalizeSqlForParsing(sql) {
      return String(sql || "")
        .trim()
        .replace(/\bfw_where_clause\b/gi, "1=1")
        .replace(/\bfw_and_clause\b/gi, "AND 1=1");
    },

    restoreFwSqlPlaceholders(sql, originalSql) {
      let out = String(sql || "");
      const original = String(originalSql || "");
      if (/\bfw_and_clause\b/i.test(original)) {
        out = out.replace(/\s+and\s+1\s*=\s*1\b/gi, " fw_and_clause");
      }
      if (/\bfw_where_clause\b/i.test(original)) {
        out = out.replace(/\bwhere\s+1\s*=\s*1\b/gi, "where fw_where_clause");
      }
      return out;
    },

    parseSelectAstFromSql(sql) {
      const normalized = this.normalizeSqlForParsing(sql);
      if (!normalized) return null;
      try {
        let ast = sqlParser.astify(normalized, { database: "PostgreSQL" });
        if (Array.isArray(ast)) ast = ast[0];
        if (!ast || ast.type !== "select" || !Array.isArray(ast.columns)) return null;
        return ast;
      } catch {
        return null;
      }
    },

    findOuterSelectSql(sql) {
      const s = String(sql || "").trim();
      if (!/^\s*with\b/i.test(s)) return s;

      let depth = 0;
      let outerStart = -1;
      for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === "(") depth++;
        else if (ch === ")") {
          depth--;
          if (depth === 0) {
            const rest = s.slice(i + 1).trim();
            if (/^select\b/i.test(rest)) outerStart = i + 1;
          }
        }
      }
      return outerStart >= 0 ? s.slice(outerStart).trim() : s;
    },

    parseSelectColumnEntry(entry) {
      const trimmed = String(entry || "").trim();
      if (!trimmed) return { expression: "", alias: null };

      const asQuoted = trimmed.match(/\bas\s+("(?:[^"]|"")*")\s*$/i);
      if (asQuoted) {
        return {
          expression: trimmed.replace(/\bas\s+("(?:[^"]|"")*")\s*$/i, "").trim(),
          alias: asQuoted[1].slice(1, -1).replace(/""/g, '"'),
        };
      }

      const asBare = trimmed.match(/\bas\s+([a-zA-Z_][\w]*)\s*$/i);
      if (asBare) {
        return {
          expression: trimmed.replace(/\bas\s+[a-zA-Z_][\w]*\s*$/i, "").trim(),
          alias: asBare[1],
        };
      }

      if (/^"[^"]+"$/.test(trimmed)) {
        return { expression: trimmed, alias: trimmed.slice(1, -1).replace(/""/g, '"') };
      }

      return { expression: trimmed, alias: null };
    },

    extractOuterSelectColumnsFallback(sql) {
      const outerSelectSql = this.findOuterSelectSql(sql);
      const match = outerSelectSql.match(/^\s*select\s+([\s\S]+?)\s+from\b/i);
      if (!match) return [];

      return this.splitSelectColumns(match[1]).map((entry) =>
        this.parseSelectColumnEntry(entry),
      );
    },

    parseSelectColumnsFromSql(sql) {
      const ast = this.parseSelectAstFromSql(sql);
      if (ast?.columns?.length) {
        return ast.columns.map((col) => ({
          col,
          expression: this.sqlifySelectColumnExpr(col),
          alias: col.as || null,
        }));
      }

      return this.extractOuterSelectColumnsFallback(sql).map((item) => ({
        col: null,
        expression: item.expression,
        alias: item.alias,
      }));
    },

    sqlifySelectColumnExpr(col) {
      if (!col) return "";
      const simpleSelectAst = {
        with: null,
        type: "select",
        options: null,
        distinct: null,
        columns: [col],
        into: { position: null },
        from: null,
        where: null,
        groupby: null,
        having: null,
        orderby: null,
        limit: null,
        locking_read: null,
        window: null,
        collate: null,
      };
      try {
        const sqlCol = sqlParser.sqlify(simpleSelectAst, { database: "PostgreSQL" });
        return sqlCol.replace(/^select\s+/i, "").trim();
      } catch {
        return "";
      }
    },

    buildSqlAliasMapFromQuery(sql) {
      const aliasMap = {};
      this.parseSelectColumnsFromSql(sql).forEach((item) => {
        if (!item.alias) return;
        aliasMap[item.alias] = item.expression || item.alias;
      });
      return aliasMap;
    },
    initPreviewMapping() {
      if (!this.vizParams || !Object.keys(this.vizParams).length) return;
      const existing = this.previewMappingRows || [];
      this.previewMappingRows = Object.entries(this.vizParams).map(
        ([label, alias]) => {
          const prev = existing.find((r) => r.axisLabel === label) || {};
          const expression = prev.expression || "";
          const displayName =
            prev.displayName != null && String(prev.displayName).trim()
              ? prev.displayName
              : expression;
          return {
            axisLabel: label,
            alias,
            expression,
            displayName,
            filterable: !!prev.filterable,
          };
        },
      );
    },

    /** Default display name from SQL expression when blank (listbox biz_name only). */
    syncPreviewRowDisplayNameFromExpression(row) {
      const expr = (row.expression || "").trim();
      if (!expr) return;
      if (!row.displayName || !String(row.displayName).trim()) {
        row.displayName = expr;
      }
    },

    setDataSourceMode(mode) {
      const next = mode === "table" ? "table" : "sql";
      if (next === "sql" && !this.reportConfigSqlEnabled) return;
      if (this.dataSourceMode === next) return;
      this.resetDataSourceSwitchState();
      this.dataSourceMode = next;
      this.syncVizParamJsonDataSourceMode(next);
    },

    toggleCatalogPanelExpanded() {
      this.catalogPanelExpanded = !this.catalogPanelExpanded;
    },

    collapseCatalogSidebar() {
      this.catalogSidebarCollapsed = true;
    },

    expandCatalogSidebar() {
      this.catalogSidebarCollapsed = false;
    },

    startCatalogPanelResize(e) {
      this.catalogPanelResizing = true;
      this._catalogResizeStartX = e.clientX;
      this._catalogResizeStartWidth = this.catalogPanelWidth;
      acquireSelectionLock({ cursor: "col-resize" });
      document.addEventListener("mousemove", this._onCatalogPanelResizeMove);
      document.addEventListener("mouseup", this._onCatalogPanelResizeEnd);
    },

    _onCatalogPanelResizeMove(e) {
      if (!this.catalogPanelResizing) return;
      const delta = e.clientX - this._catalogResizeStartX;
      const next = Math.min(
        520,
        Math.max(200, this._catalogResizeStartWidth + delta),
      );
      this.catalogPanelWidth = next;
    },

    _onCatalogPanelResizeEnd() {
      this.stopCatalogPanelResize();
    },

    stopCatalogPanelResize() {
      this.catalogPanelResizing = false;
      releaseSelectionLock();
      document.removeEventListener("mousemove", this._onCatalogPanelResizeMove);
      document.removeEventListener("mouseup", this._onCatalogPanelResizeEnd);
    },

    onCopyableRegionKeydown,

    applyCatalogSearchTreeExpansion() {
      const q = this.catalogSearchNormalized;
      if (!q) return;
      const catalog = this.resolvedTableCatalog;
      for (const schema of Object.keys(catalog)) {
        const schemaMatch = schema.toLowerCase().includes(q);
        const tables = Object.keys(catalog[schema] || {});
        const hasTableMatch = tables.some((t) => t.toLowerCase().includes(q));
        if (schemaMatch || hasTableMatch) {
          this.$set(this.treeExpandedSchemas, schema, true);
        }
      }
    },

    applyDefaultDataSourceMode() {
      const fromJson = normalizeDataSourceMode(this.vizParamJson);
      const mode = fromJson || this.defaultDataSourceMode;
      this.dataSourceMode = mode;
      this.syncVizParamJsonDataSourceMode(mode);
      if (mode === "table") {
        this.fetchTableCatalog().then(() => {
          this.$nextTick(() => this.initTableModeFromCatalog());
        });
      }
    },

    syncVizParamJsonDataSourceMode(mode) {
      const next = {
        ...(this.vizParamJson && typeof this.vizParamJson === "object"
          ? this.vizParamJson
          : {}),
        dataSourceMode: dataSourceModeToVizParamValue(mode),
      };
      this.vizParamJson = next;
    },

    parseVizParamJsonFromRow(row) {
      if (!row) return {};
      const raw = row.viz_param_json ?? row.vizParamJson;
      if (!raw) return {};
      if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
          return {};
        }
      }
      return {};
    },

    onAdminVariableMappingRowChange({ row, field, value, rowPatch }) {
      if (this.isTableDataSource) {
        const idx = (this.tableModeColumnRows || []).findIndex(
          (r) => r && r.columnName === row.columnName,
        );
        if (idx < 0) return;
        const patch = rowPatch
          ? { ...rowPatch, [field]: value }
          : { [field]: value };
        this.$set(this.tableModeColumnRows, idx, {
          ...this.tableModeColumnRows[idx],
          ...patch,
        });
        if (field === "displayType" || rowPatch?.displayType) {
          this.onTableColumnDisplayTypeChange(this.tableModeColumnRows[idx]);
        }
        this.syncGeneratedDataSqlFromTable();
        this.invalidatePreview();
        return;
      }
      const idx = (this.previewTableBarRows || []).findIndex(
        (r) => r && r.required === row.required && r.expr === row.expr,
      );
      if (idx < 0) return;
      const patch = rowPatch
        ? { ...rowPatch, [field]: value }
        : { [field]: value };
      this.$set(this.previewTableBarRows, idx, {
        ...this.previewTableBarRows[idx],
        ...patch,
      });
      this.invalidatePreview();
    },

    /** Clears SQL/table workspace state when toggling data source mode. */
    resetDataSourceSwitchState() {
      this.dataSqlId = "";
      this.defaultChartData.datasql = "";
      this.defaultChartData.reportHeaderColumnDp = {};
      this.previewChartsVisible = false;
      this.previewLoading = false;
      this.previewError = "";
      this.previewErrorQuery = "";
      this.previewData = [];
      this.previewConfig = {};
      this.previewTableBarRows = [];
      this.previewMappingRows = [];
      this.initPreviewMapping();
      this.selectedSchema = "";
      this.selectedTableName = "";
      this.tableModeColumnRows = [];
      this.treeExpandedSchemas = {};
      this.treeExpandedTables = {};
      this.catalogSidebarCollapsed = false;
      this.catalogPanelExpanded = true;
      this.catalogSearchQuery = "";
      this.tableHeaders = [];
      this.menuName = "";
      this.screenName = "";
      this.x_axisName = "";
      this.y_axisName = "";
      this.vizTitleName = "";
      this.x_axisTooltip = "";
      this.y_axisTooltip = "";
      this.x_axisDataName = "";
      this.y_axisDataName = "";
    },

    async fetchTableCatalog() {
      if (this.tableCatalogFetched) return;
      try {
        const data = await fetchTableCatalogCached();
        this.tableCatalog = data;
      } catch (e) {
        console.warn("table-catalog fetch failed", e);
        this.tableCatalog = {};
      } finally {
        this.tableCatalogFetched = true;
      }
    },

    invalidatePreview() {
      this.previewChartsVisible = false;
      this.previewError = "";
      this.previewErrorQuery = "";
      this.$nextTick(() => {
        const el = this.$refs.r1c2;
        if (el) el.innerHTML = "";
      });
    },

    treeTableKey(schema, table) {
      return `${schema}\x1f${table}`;
    },

    initTableModeFromCatalog() {
      if (!this.isTableDataSource) return;
      const catalog = this.resolvedTableCatalog;
      const schemas = Object.keys(catalog).sort();
      if (!schemas.length) {
        this.selectedSchema = "";
        this.selectedTableName = "";
        this.tableModeColumnRows = [];
        return;
      }
      if (this.selectedSchema && !catalog[this.selectedSchema]) {
        this.selectedSchema = "";
        this.selectedTableName = "";
      }
      if (!this.selectedSchema && schemas.length) {
        this.selectedSchema = schemas[0];
      }
      const tables =
        this.selectedSchema && catalog[this.selectedSchema]
          ? Object.keys(catalog[this.selectedSchema]).sort()
          : [];
      if (
        (!this.selectedTableName && tables.length) ||
        (this.selectedTableName &&
          this.selectedSchema &&
          !catalog[this.selectedSchema][this.selectedTableName])
      ) {
        this.selectedTableName = tables.length ? tables[0] : "";
      }
      this.expandTreeForSelection();
      this.refreshTableModeRowsFromCatalog();
      this.syncGeneratedDataSqlFromTable();
    },

    expandTreeForSelection() {
      if (this.selectedSchema) {
        this.$set(this.treeExpandedSchemas, this.selectedSchema, true);
      }
      if (this.selectedSchema && this.selectedTableName) {
        this.$set(
          this.treeExpandedTables,
          this.treeTableKey(this.selectedSchema, this.selectedTableName),
          true,
        );
      }
    },

    toggleTreeSchema(schema) {
      const next = !this.treeExpandedSchemas[schema];
      this.$set(this.treeExpandedSchemas, schema, next);
    },

    toggleTreeTable(schema, table) {
      const k = this.treeTableKey(schema, table);
      this.$set(this.treeExpandedTables, k, !this.treeExpandedTables[k]);
    },

    selectCatalogTable(schema, table) {
      this.selectedSchema = schema;
      this.selectedTableName = table;
      this.expandTreeForSelection();
      this.onSchemaOrTableChange();
    },

    sortedCatalogTables(schema) {
      const catalog = this.resolvedTableCatalog;
      if (!schema || !catalog[schema]) return [];
      return Object.keys(catalog[schema]).sort();
    },

    filteredCatalogTables(schema) {
      const tables = this.sortedCatalogTables(schema);
      const q = this.catalogSearchNormalized;
      if (!q) return tables;
      if (schema.toLowerCase().includes(q)) return tables;
      return tables.filter((t) => t.toLowerCase().includes(q));
    },

    catalogColumns(schema, table) {
      const catalog = this.resolvedTableCatalog;
      if (!schema || !table || !catalog[schema] || !catalog[schema][table]) {
        return [];
      }
      return catalog[schema][table];
    },

    columnTypeKind(dataType) {
      const t = this.inferDisplayTypeFromSqlType(dataType);
      if (t === "Date") return "date";
      if (t === "Metric") return "num";
      return "text";
    },

    shortTypeLabel(dataType) {
      let s = (dataType || "").replace(/fw_core\./gi, "").replace(/"/g, "");
      if (s.length > 20) {
        return `${s.slice(0, 18)}…`;
      }
      return s || "?";
    },

    inferDisplayTypeFromSqlType(dataType) {
      const dt = (dataType || "").toLowerCase();
      if (
        dt.includes("fw_timestamp") ||
        dt.includes("timestamp") ||
        dt === "date" ||
        dt === "datetime" ||
        /\b(date|time)\b/.test(dt)
      ) {
        return "Date";
      }
      if (
        /\bserial\d+\b/.test(dt) ||
        /\b(int|integer|bigint|smallint|numeric|decimal|float|real|double|money|serial|bigserial|number)\b/.test(
          dt,
        )
      ) {
        return "Metric";
      }
      if (
        dt.includes("fw_text") ||
        /\b(char|varchar|nvarchar|text|string|var)\b/.test(dt) ||
        dt.includes("varchar") ||
        dt === "uuid"
      ) {
        return "Dimension";
      }
      return "Dimension";
    },

    normalizeTableModeDisplayType(catalogColumnType) {
      const raw = String(catalogColumnType || "").trim().toLowerCase();
      if (raw === "metric" || raw === "numeric" || raw==="fw_numeric" || raw==="fw_core_etl.fw_num_metric"
       || raw==="fw_core_etl.fw_num_metric_long" || raw==="fw_core.fw_num_metric") return "Metric";
      if (raw === "date") return "Date";
      if (raw === "identifier") return "Identifier";
      return "Dimension";
    },

    columnNameToDisplayName(columnName) {
      const raw = String(columnName || "").trim();
      if (!raw) return "";
      return raw
        .split("_")
        .filter((part) => part.length > 0)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
    },

    setAllTableColumnInclude(include) {
      (this.tableModeColumnRows || []).forEach((row) => {
        if (row) row.include = include;
      });
    },

    refreshTableModeRowsFromCatalog() {
      if (!this.isTableDataSource) {
        this.tableModeColumnRows = [];
        return;
      }
      const catalog = this.resolvedTableCatalog;
      const schema = this.selectedSchema;
      const table = this.selectedTableName;
      const cols =
        schema && table && catalog[schema] && catalog[schema][table]
          ? catalog[schema][table]
          : [];

      let seq = 1;
      this.tableModeColumnRows = cols.map((c) => {
        const displayType = this.normalizeTableModeDisplayType(c.dataType);
        let aggregate = "";
        if (displayType === "Metric") aggregate = "SUM";
        else if (displayType === "Date") aggregate = "YEAR";
        return {
          seq: seq++,
          columnName: c.name,
          include: true,
          filterable: false,
          displayName: this.columnNameToDisplayName(c.name),
          dataType: c.dataType,
          displayType,
          aggregate,
          graph: "no",
        };
      });
    },

    onSchemaOrTableChange() {
      this.refreshTableModeRowsFromCatalog();
      this.syncGeneratedDataSqlFromTable();
      this.invalidatePreview();
    },

    onTableColumnDisplayTypeChange(row) {
      const agg = String(row.aggregate || "").trim().toUpperCase();
      if (row.displayType === "Metric") {
        if (
          !agg ||
          this.isDateAggregateValue(row.aggregate) ||
          this.isDimensionOnlyAggregateValue(row.aggregate)
        ) {
          row.aggregate = "SUM";
        }
      } else if (row.displayType === "Date") {
        if (!agg || !this.isDateAggregateValue(row.aggregate)) {
          row.aggregate = "YEAR";
        }
      } else if (
        row.displayType === "Dimension" ||
        row.displayType === "Identifier"
      ) {
        if (!this.isDimensionAggregateValue(row.aggregate)) {
          row.aggregate = "";
        }
      } else {
        row.aggregate = "";
      }
    },

    isDimensionOnlyAggregateValue(value) {
      const v = String(value || "").trim().toUpperCase();
      return v === "COUNT" || v === "COUNT_DISTINCT";
    },

    isDimensionAggregateValue(value) {
      const v = String(value || "").trim().toUpperCase();
      return v === "" || v === "COUNT" || v === "COUNT_DISTINCT";
    },

    isDateColumnRow(row) {
      if (!row) return false;
      return row.displayType === "Date";
    },

    isDateAggregateValue(value) {
      const v = String(value || "").trim().toUpperCase();
      return v === "YEAR" || v === "QUARTER" || v === "MONTH" || v === "YYYYMM";
    },

    aggregateSelectEnabledForRow(row) {
      if (!row) return false;
      return (
        row.displayType === "Metric" ||
        row.displayType === "Dimension" ||
        row.displayType === "Identifier" ||
        this.isDateColumnRow(row)
      );
    },

    aggregateOptionsForRow(row) {
      if (!row) return [{ value: "", text: "—" }];
      if (row.displayType === "Metric") {
        return appendCountDistinctOption(TABLE_MODE_AGG_OPTIONS_METRIC);
      }
      if (this.isDateColumnRow(row)) {
        return appendCountDistinctOption(TABLE_MODE_AGG_OPTIONS_DATE);
      }
      if (
        row.displayType === "Dimension" ||
        row.displayType === "Identifier"
      ) {
        return TABLE_MODE_AGG_OPTIONS_DIMENSION;
      }
      return appendCountDistinctOption([
        { value: "", text: "—" },
        { value: "COUNT", text: "COUNT" },
      ]);
    },

    tableModeGraphSelectEnabledForRow(row) {
      if (!row) return false;
      return row.displayType === "Metric";
    },

    tableModeRowWantsGraph(row) {
      if (!this.tableModeGraphSelectEnabledForRow(row)) return false;
      const graph = String(row.graph || "").trim().toLowerCase();
      return graph === "yes" || graph === "y" || graph === "true";
    },

    /** PostgreSQL date grain for catalog table mode (casts fw_timestamp/date columns). */
    buildDatePartSqlExpr(quotedCol, grain) {
      const ts = `(${quotedCol})::timestamp`;
      switch (String(grain || "").trim().toUpperCase()) {
        case "YEAR":
          return `EXTRACT(YEAR FROM ${ts})`;
        case "QUARTER":
          return `TO_CHAR(${ts}, 'YYYY"Q"Q')`;
        case "MONTH":
          return `EXTRACT(MONTH FROM ${ts})`;
        case "YYYYMM":
          return `TO_CHAR(${ts}, 'YYYYMM')`;
        default:
          return quotedCol;
      }
    },

    buildTableColumnSelectExpr(row, quotedCol) {
      const agg = (row.aggregate || "").trim().toUpperCase();
      if (agg === "COUNT_DISTINCT") {
        return `COUNT(DISTINCT ${quotedCol})`;
      }
      if (agg === "COUNT") {
        return `COUNT(${quotedCol})`;
      }
      if (row.displayType === "Metric" && agg) {
        return `${agg}(${quotedCol})`;
      }
      if (this.isDateColumnRow(row) && agg) {
        return this.buildDatePartSqlExpr(quotedCol, agg);
      }
      return quotedCol;
    },

    /** True when the row contributes a SQL aggregate (COUNT, SUM, etc.) in SELECT. */
    rowUsesSelectAggregate(row) {
      if (!row) return false;
      const agg = (row.aggregate || "").trim().toUpperCase();
      if (agg === "COUNT" || agg === "COUNT_DISTINCT") return true;
      return row.displayType === "Metric" && !!agg;
    },

    assignmentNeedsGroupByClause(assignments) {
      return (assignments || []).some((a) => this.rowUsesSelectAggregate(a.row));
    },

    /** GROUP BY only non-aggregated dimension expressions (plain cols, date grains). */
    buildGroupByClauseFromAssignments(assignments) {
      const dims = (assignments || []).filter((a) => !a.isMetric);
      const parts = dims
        .filter((a) => !this.rowUsesSelectAggregate(a.row))
        .map((a) => a.expr);
      return parts.join(", ");
    },

    syncGeneratedDataSqlFromTable() {
      if (!this.isTableDataSource) return;
      const built = this.buildTableModeSqlAndHeaderMap();
      this.dataSqlId = built && built.newSql ? built.newSql : "";
    },

    /**
     * One entry per catalog column marked “include”; aliases match chart SQL (`dim{N}_name`, `metric{N}`).
     */
    buildTableIncludedSelectAssignments() {
      if (!this.selectedSchema || !this.selectedTableName) return [];
      const included = (this.tableModeColumnRows || []).filter((r) => r.include);
      let dimIndex = 1;
      let metricIndex = 1;
      const assignments = [];

      for (const row of included) {
        const colId = row.columnName.replace(/"/g, '""');
        const quotedCol = `"${colId}"`;
        const expr = this.buildTableColumnSelectExpr(row, quotedCol);

        const isMetric = row.displayType === "Metric";
        const alias = isMetric
          ? `metric${metricIndex++}`
          : `dim${dimIndex++}_name`;
        assignments.push({ row, expr, alias, isMetric });
      }
      return assignments;
    },

    /**
     * Builds listbox DISTINCT / GROUP BY query and `{ listbox_dim_name, biz_name }[]`
     * for `/api/sp-config-menu`. Assignments shape: `{ expr, alias, isMetric, row?, axisLabel? }`.
     */
    composeListboxSqlFromAssignments(assignments, fromClause) {
      if (!assignments.length || !fromClause) {
        return { listboxSql: "", listboxColumnMapping: [] };
      }

      const dims = assignments.filter((a) => !a.isMetric);
      const metrics = assignments.filter((a) => a.isMetric);

      const listboxColumnMapping = assignments.map((a) => {
        const columnName =
          (a.row && String(a.row.columnName || "").trim()) ||
          String(a.expr || "").trim() ||
          a.alias;
        let biz = "";
        if (a.row) {
          if (a.row.displayName != null && String(a.row.displayName).trim()) {
            biz = String(a.row.displayName).trim();
          } else if (a.row.label != null && String(a.row.label).trim()) {
            biz = String(a.row.label).trim();
          }
        }
        return {
          listbox_dim_name: columnName,
          biz_name: biz || columnName,
        };
      });

      let listboxSql = "";

      const selectExpr = (a) => a.expr;

      const needsGroupBy = this.assignmentNeedsGroupByClause(assignments);
      const groupBy = needsGroupBy
        ? this.buildGroupByClauseFromAssignments(assignments)
        : "";

      if (dims.length === 0 && metrics.length === 0) {
        listboxSql = "";
      } else if (dims.length === 0) {
        const metricSelect = metrics.map(selectExpr).join(", ");
        listboxSql = needsGroupBy
          ? `SELECT ${metricSelect} FROM ${fromClause}`
          : `SELECT DISTINCT ${metricSelect} FROM ${fromClause}`;
      } else if (metrics.length === 0) {
        const dimSelect = dims.map(selectExpr).join(", ");
        if (needsGroupBy) {
          listboxSql = groupBy
            ? `SELECT ${dimSelect} FROM ${fromClause} GROUP BY ${groupBy}`
            : `SELECT ${dimSelect} FROM ${fromClause}`;
        } else {
          listboxSql = `SELECT DISTINCT ${dimSelect} FROM ${fromClause}`;
        }
      } else {
        const dimSelect = dims.map(selectExpr).join(", ");
        const metricSelect = metrics.map(selectExpr).join(", ");
        if (needsGroupBy) {
          listboxSql = groupBy
            ? `SELECT ${dimSelect}, ${metricSelect} FROM ${fromClause} GROUP BY ${groupBy}`
            : `SELECT ${dimSelect}, ${metricSelect} FROM ${fromClause}`;
        } else {
          listboxSql = `SELECT DISTINCT ${dimSelect}, ${metricSelect} FROM ${fromClause}`;
        }
      }

      listboxSql = listboxSql ? this.appendDynamicClause(listboxSql) : "";

      return { listboxSql, listboxColumnMapping };
    },

    sqlifyAstFromClause(ast) {
      if (!ast || !Array.isArray(ast.from) || !ast.from.length) return "";
      try {
        const stub = {
          with: ast.with ?? null,
          type: "select",
          options: ast.options ?? null,
          distinct: null,
          columns: [
            {
              expr: { type: "column_ref", table: null, column: "*", collate: null },
              as: null,
            },
          ],
          into: { position: null },
          from: ast.from,
          where: null,
          groupby: null,
          having: null,
          orderby: null,
          limit: null,
          locking_read: null,
          window: null,
          collate: null,
        };
        const s = sqlParser.sqlify(stub, { database: "PostgreSQL" });
        const m = s.match(/^\s*select\s+(?:distinct\s+|all\s+)?\*\s+from\s+([\s\S]+)$/i);
        return m ? m[1].trim() : "";
      } catch {
        return "";
      }
    },

    buildSqlModeListboxAssignments() {
      const sql = (this.dataSqlId || "").trim();
      if (!sql)
        return { assignments: [], fromClause: "" };

      const ast = this.parseSelectAstFromSql(sql);
      if (!ast || !Array.isArray(ast.from)) {
        return { assignments: [], fromClause: "" };
      }

      const fromClause = this.sqlifyAstFromClause(ast);
      let dimIdx = 1;
      let metricIdx = 1;
      const assignments = [];

      const rows = this.previewMappingRows || [];
      for (const row of rows) {
        if (!row.filterable) continue;
        const expr = (row.expression || "").trim();
        if (!expr) continue;
        const isMetric =
          typeof row.alias === "string" &&
          row.alias.toLowerCase().startsWith("metric");
        const alias = isMetric ? `metric${metricIdx++}` : `dim${dimIdx++}_name`;
        assignments.push({
          row,
          expr,
          alias,
          isMetric,
          axisLabel: row.axisLabel,
        });
      }

      return { assignments, fromClause };
    },

    /** Resolves catalog / SQL datasource listbox payloads for saving. */
    buildListboxPersistPayload() {
      if (!this.previewMappingSectionVisible) {
        return { listboxSql: "", listboxColumnMapping: [] };
      }
      if (this.vizFunction === "tableBarChart") {
        if (this.isTableDataSource) {
          const qualifiedTable = `${this.selectedSchema}.${this.selectedTableName}`;
          const assignments = this.buildTableIncludedSelectAssignments().filter(
            (a) => a.row.filterable,
          );
          return this.composeListboxSqlFromAssignments(
            assignments,
            qualifiedTable,
          );
        }
        return this.buildTableBarSqlListboxPayload();
      }
      if (this.isTableDataSource) {
        const qualifiedTable = `${this.selectedSchema}.${this.selectedTableName}`;
        const assignments = this.buildTableIncludedSelectAssignments().filter(
          (a) => a.row.filterable,
        );
        return this.composeListboxSqlFromAssignments(assignments, qualifiedTable);
      }
      const { assignments, fromClause } = this.buildSqlModeListboxAssignments();
      if (!assignments.length || !fromClause) {
        return { listboxSql: "", listboxColumnMapping: [] };
      }
      return this.composeListboxSqlFromAssignments(assignments, fromClause);
    },

    /** Listbox SQL + mapping when viz is table-bar and data source is raw SQL (`previewTableBarRows`). */
    buildTableBarSqlListboxPayload() {
      const sql = (this.dataSqlId || "").trim();
      if (!sql || !((this.previewTableBarRows || []).length > 0)) {
        return { listboxSql: "", listboxColumnMapping: [] };
      }

      const ast = this.parseSelectAstFromSql(sql);
      if (!ast || !Array.isArray(ast.columns)) {
        return { listboxSql: "", listboxColumnMapping: [] };
      }

      const fromClause = this.sqlifyAstFromClause(ast);
      if (!fromClause) {
        return { listboxSql: "", listboxColumnMapping: [] };
      }

      const rows = this.previewTableBarRows || [];
      let dimIndex = 1;
      let metricIndex = 1;
      const assignments = [];

      for (let idx = 0; idx < rows.length; idx++) {
        const row = rows[idx];
        const colAst = ast.columns[idx] || null;
        const exprAst =
          colAst && colAst.expr
            ? colAst.expr
            : {
                type: "column_ref",
                table: null,
                column: row.expr,
                collate: null,
              };

        let exprStr = "";
        try {
          const simpleSelectAst = {
            with: null,
            type: "select",
            options: null,
            distinct: null,
            columns: [{ expr: exprAst, as: null }],
            into: { position: null },
            from: null,
            where: null,
            groupby: null,
            having: null,
            orderby: null,
            limit: null,
            locking_read: null,
            window: null,
            collate: null,
          };
          const sqlCol = sqlParser.sqlify(simpleSelectAst, {
            database: "PostgreSQL",
          });
          exprStr = sqlCol.replace(/^select\s+/i, "").trim();
        } catch {
          exprStr = (row.expr || "").trim();
        }

        const isMetric =
          typeof row.type === "string" &&
          row.type.trim().toLowerCase() === "metric";
        const alias = isMetric
          ? `metric${metricIndex++}`
          : `dim${dimIndex++}_name`;

        if (!row.filterable) continue;

        assignments.push({
          expr: exprStr,
          alias,
          isMetric,
          row: {
            columnName: (row.expr || exprStr || "").trim(),
            displayName: row.label,
          },
        });
      }

      return this.composeListboxSqlFromAssignments(assignments, fromClause);
    },

    buildTableModeSqlAndHeaderMap() {
      const assignments = this.buildTableIncludedSelectAssignments();
      if (!assignments.length) return null;

      const qualifiedTable = `${this.selectedSchema}.${this.selectedTableName}`;
      const selectParts = assignments.map((a) => `${a.expr} AS ${a.alias}`);
      const headerMap = {};
      let metricNum = 0;
      for (const a of assignments) {
        const label = (a.row.displayName || a.row.columnName).trim();
        headerMap[label] = a.alias;
        if (a.isMetric) {
          metricNum += 1;
          if (this.tableModeRowWantsGraph(a.row)) {
            headerMap[`${label}#`] = `graph${metricNum}`;
          }
        }
      }

      let newSql = `SELECT ${selectParts.join(", ")} FROM ${qualifiedTable}`;
      if (this.assignmentNeedsGroupByClause(assignments)) {
        const groupBy = this.buildGroupByClauseFromAssignments(assignments);
        if (groupBy) newSql += ` GROUP BY ${groupBy}`;
      }

      return { newSql, headerMap };
    },

    /** Removes a top-level trailing LIMIT (preview-only; not persisted on save). */
    stripTopLevelLimit(sql) {
      const trimmed = (sql || "").trim().replace(/;+\s*$/g, "");
      if (!trimmed) return trimmed;

      const lower = trimmed.toLowerCase();
      let depth = 0;
      let limitStart = -1;
      for (let i = 0; i < lower.length; i++) {
        const ch = lower[i];
        if (ch === "(") depth++;
        else if (ch === ")" && depth > 0) depth--;
        if (depth === 0 && lower.startsWith("limit", i)) {
          limitStart = i;
          break;
        }
      }
      if (limitStart === -1) return trimmed;
      return trimmed.slice(0, limitStart).trimEnd();
    },

    /** LIMIT 500 for /api/fetchData preview only. */
    appendPreviewLimit(sql) {
      const base = this.stripTopLevelLimit(sql);
      if (!base) return base;
      return `${base} LIMIT 500`;
    },

    /** DataSQL persisted via postConfigMenu (no preview LIMIT). */
    buildPersistDataSql() {
      let sql = "";
      if (this.isTableDataSource) {
        const built = this.buildTableModeSqlAndHeaderMap();
        sql = (built && built.newSql) || (this.dataSqlId || "").trim();
      } else {
        sql = this.stripTopLevelLimit((this.dataSqlId || "").trim());
      }
      return sql ? this.appendDynamicClause(sql) : "";
    },
    async renderExistingChart() {
      let vizData = this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
      console.log("vizData", vizData);
      if (!vizData || vizData.length === 0) {
        if (this.$refs.r1c1) this.$refs.r1c1.innerHTML = "";
        return;
      }

      // Clear existing chart so we don't see the old one while new data loads
      if (this.$refs.r1c1) this.$refs.r1c1.innerHTML = "";

      let allVizType = this.$store.state.vizType;
      let filter = allVizType.filter(
        (t) => t.viztypeId === vizData[0].vizletId,
      )[0];
      let vizFunction = filter.viztypeFunctionName;
      this.vizFunction = vizFunction;
      if (vizFunction === "tableBarChart") {
        if (this.$refs.r1c1) this.$refs.r1c1.innerHTML = "";
        return;
      }

      await this.$nextTick();
      const divId = this.$refs.r1c1;
      if (!divId) return;

      const v = vizData[0];
      this.defaultChartData.x1D3ScaleType = v.x1_d3scaleType || "bandScale";
      this.defaultChartData.x2D3ScaleType = v.x2_d3scaleType || "bandScale";
      this.defaultChartData.y1D3ScaleType = v.y1_d3scaleType || "linearScale";
      this.defaultChartData.y2D3ScaleType = v.y2_d3scaleType || "linearScale";

      let margin = {
        top: 50,
        right: 150,
        bottom: 100,
        left: 80,
      };

      let vizunit = vizData[0].vizletSequence;

      let getChartInfo = (vizunit) => {
        let f = vizData[0];
        f.chartWidth = 800;
        f.chartHeight = 300;
        f.axisFontSize = 14;
        f.axisLabelFontSize = 12;
        if (f.length === 0)
          return new FwVizChartInfo(vizunit, margin, {}, false, []);
        return new FwVizChartInfo(
          vizunit,
          margin,
          f,
          vizData[0].visibleFlag,
          f,
        );
      };

      let chartData = getChartInfo(vizunit);
      console.log("chartData", chartData, this.vizFunction);

      var url =
        "/api/fetchDataWithParam/" +
        chartData.data.datasqlId +
        "/" +
        "CommonVizClass";
      const controller = apiAbort.new();

      axios
        .post(
          url,
          {},
          {
            signal: controller.signal,
            headers: {
              "info-params": JSON.stringify({
                menuId: this.$route.params.menuId,
                submenuId: this.$route.params.submenuId,
              }),
              "Content-Type": "application/json",
            },
          },
        )
        .then((resp) => {
          this.$commonFn.renderChartCompare(
            "CommonVizClass", // existing viz
            chartData,
            divId,
            this.vizFunction,
            resp.data,
            {},
          );
        });
    },
    syncTableBarPreviewRowsFromSql(sql) {
      if (!sql || !sql.trim()) {
        this.previewTableBarRows = [];
        return;
      }

      const parsedColumns = this.parseSelectColumnsFromSql(sql);
      if (!parsedColumns.length) {
        this.previewTableBarRows = [];
        return;
      }

      const existing = this.previewTableBarRows || [];
      const headerMap =
        this.defaultChartData?.reportHeaderColumnDp ||
        this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp ||
        {};
      const newRows = parsedColumns.map((item, idx) => {
        const prev = existing[idx] || {};
        const expression = item.expression || "";
        const label = resolveSqlMappingRowLabel(prev, item, expression);
        const headerAlias =
          label && headerMap[label] != null
            ? String(headerMap[label]).trim()
            : "";
        const type =
          prev.type === "Metric" ||
          (headerAlias && headerAlias.toLowerCase().startsWith("metric"))
            ? "Metric"
            : "Dimension";
        const graph =
          type === "Metric"
            ? resolveSqlMappingRowGraph(headerMap, {
                label,
                headerDisplayKey: label,
                metricAlias: headerAlias,
                prevGraph: prev.graph,
              })
            : "no";

        return {
          required: `Col${idx + 1}`,
          expr: expression,
          label,
          type,
          graph,
          filterable: !!prev.filterable,
        };
      });
      this.previewTableBarRows = newRows;
    },

    buildTableBarPreview(sql) {
      if (!sql) return { newSql: "", headerMap: {} };

      const rows = this.previewTableBarRows || [];
      const originalDatasql = this.fetchDefaultvalues?.[0]?.datasql || "";
      const built = buildVizletSqlModeColumnLayout({
        rawSql: sql,
        mappingRows: rows,
        existingHeaderMap:
          this.defaultChartData?.reportHeaderColumnDp ||
          this.vizParams ||
          {},
        originalDatasql,
      });
      if (built.tableBarSql) {
        return { newSql: built.tableBarSql, headerMap: built.headerMap || {} };
      }
      return this.buildTableBarPreviewFallback(sql, rows);
    },

    buildTableBarPreviewFallback(sql, rows) {
      if (!sql || !rows.length) return { newSql: sql || "", headerMap: {} };

      const outerSelectSql = this.findOuterSelectSql(sql);
      const fromMatch = outerSelectSql.match(/^\s*select\s+[\s\S]+?\s+from\s+([\s\S]+)$/i);
      if (!fromMatch) return { newSql: sql, headerMap: {} };

      const parsedColumns = this.parseSelectColumnsFromSql(sql);
      let dimIndex = 1;
      let metricIndex = 1;
      const headerEntries = [];
      const newSelectCols = [];

      rows.forEach((row, idx) => {
        const parsed = parsedColumns[idx] || {};
        const expr = (row.expr || parsed.expression || "").trim();
        if (!expr) return;

        const label = row.label || parsed.alias || expr;
        const isMetric =
          typeof row.type === "string" &&
          row.type.trim().toLowerCase() === "metric";
        const wantsGraph =
          isMetric &&
          typeof row.graph === "string" &&
          row.graph.trim().length > 0 &&
          row.graph.toLowerCase().startsWith("yes");

        let alias;
        let graphAlias = null;
        if (isMetric) {
          alias = `metric${metricIndex++}`;
          graphAlias = wantsGraph ? `graph${metricIndex - 1}` : null;
        } else {
          alias = `dim${dimIndex++}_name`;
        }

        newSelectCols.push(`${expr} as ${alias}`);
        headerEntries.push({
          key: label,
          value: alias,
          graphKey: wantsGraph ? `${label}#` : null,
          graphValue: graphAlias,
        });
      });

      if (!newSelectCols.length) return { newSql: sql, headerMap: {} };

      const prefix = sql.slice(0, sql.indexOf(outerSelectSql)).trim();
      let newSql = prefix ? `${prefix} ` : "";
      newSql += `select ${newSelectCols.join(", ")} from ${fromMatch[1].trim()}`;
      newSql = this.restoreFwSqlPlaceholders(newSql, sql);

      const headerMap = {};
      headerEntries.forEach((e) => {
        headerMap[e.key] = e.value;
      });
      headerEntries.forEach((e) => {
        if (e.graphKey && e.graphValue) {
          headerMap[e.graphKey] = e.graphValue;
        }
      });

      return { newSql, headerMap };
    },

    standardizeSQL(sql) {
      if (!sql) return { newSql: "", headerMap: {} };

      const selectMatch = sql.match(/select([\s\S]*?)from/i);
      if (!selectMatch) return { newSql: sql, headerMap: {} };

      const selectPart = selectMatch[1];
      const fromPart = sql.substring(
        selectMatch.index + selectMatch[0].length - 4,
      );

      // Remove line breaks
      const cleanedSelect = selectPart.replace(/\n/g, " ");

      // Split columns safely
      const columns = cleanedSelect.match(/(?:[^,(]|\([^)]*\))+/g);

      let dimIndex = 1;
      let metricIndex = 1;

      const headerMap = {};
      const newColumns = [];

      columns.forEach((col) => {
        let column = col.trim();

        const isAggregate = /(sum|avg|count|min|max|round)/i.test(column);

        // Remove existing alias if present
        column = column.replace(/\bas\s+[a-zA-Z0-9_]+/i, "").trim();

        if (!isAggregate) {
          const alias = `dim${dimIndex}_name`;
          newColumns.push(`${column} as ${alias}`);
          headerMap[column] = alias;
          dimIndex++;
        } else {
          const alias = `metric${metricIndex}`;
          newColumns.push(`${column} as ${alias}`);
          headerMap[column] = alias;
          metricIndex++;
        }
      });

      const newSelectClause = `select ${newColumns.join(", ")} `;
      const newSql = newSelectClause + fromPart;

      return { newSql, headerMap };
    },

    appendDynamicClause(sql) {
      if (!sql) return "";

      const lowerSql = sql.toLowerCase();

      // Avoid duplicate injection
      if (
        lowerSql.includes("fw_where_clause") ||
        lowerSql.includes("fw_and_clause")
      ) {
        return sql;
      }

      const hasWhere = /\bwhere\b/i.test(sql);
      const clause = hasWhere ? "fw_and_clause" : "fw_where_clause";

      // Find the first top-level GROUP BY / ORDER BY / HAVING / LIMIT,
      // ignoring those that appear inside parentheses (e.g. window functions).
      let depth = 0;
      let splitIndex = -1;
      for (let i = 0; i < lowerSql.length; i++) {
        const ch = lowerSql[i];
        if (ch === "(") depth++;
        else if (ch === ")" && depth > 0) depth--;

        if (depth === 0) {
          if (
            lowerSql.startsWith("group by", i) ||
            lowerSql.startsWith("order by", i) ||
            lowerSql.startsWith("having", i) ||
            lowerSql.startsWith("limit", i)
          ) {
            splitIndex = i;
            break;
          }
        }
      }

      if (splitIndex !== -1) {
        // Inject clause before the top-level GROUP/ORDER/HAVING/LIMIT
        return `${sql.slice(0, splitIndex)}\n${clause}\n${sql.slice(
          splitIndex,
        )}`;
      }

      // If no grouping/ordering keywords, append safely at end
      return `${sql}\n${clause}`;
    },

    validateSQL(sql) {
      if (!sql || !sql.trim()) {
        this.showToast("SQL query cannot be empty", "danger");
        return false;
      }

      // Normalize: trim and remove trailing semicolons (allow a single statement ending with ;)
      const trimmed = sql.trim();
      const sqlNoTrailingSemi = trimmed.replace(/;+\s*$/g, "");

      const forbiddenKeywords = [
        "update",
        "delete",
        "truncate",
        "drop",
        "alter",
        "insert",
        "create",
        "grant",
        "revoke",
      ];

      const lowerSql = sqlNoTrailingSemi.toLowerCase();

      // ❌ Block multiple statements (very important)
      if (lowerSql.includes(";")) {
        this.showToast("Multiple SQL statements are not allowed", "danger");
        return false;
      }

      // ❌ Block dangerous keywords
      for (let keyword of forbiddenKeywords) {
        const regex = new RegExp(`\\b${keyword}\\b`, "i");
        if (regex.test(sql)) {
          this.showToast(
            `"${keyword.toUpperCase()}" operations are not allowed`,
            "danger",
          );
          return false;
        }
      }

      // ❌ Must start with SELECT
      if (!/^\s*(select|with)\b/i.test(sqlNoTrailingSemi)) {
        this.showToast("Only SELECT queries are allowed", "danger");
        return false;
      }

      return true;
    },

    showToast(message, variant = "danger") {
      this.$bvToast.toast(message, {
        title: "SQL Validation Error",
        variant: variant,
        solid: true,
        autoHideDelay: 4000,
      });
    },

    buildPreviewSQL(sql) {
      if (!sql) return { newSql: "", headerMap: {} };

      const ast = this.parseSelectAstFromSql(sql);
      if (!ast || !Array.isArray(ast.columns)) {
        return { newSql: sql, headerMap: {} };
      }

      const expressionToAlias = {};
      this.previewMappingRows.forEach((row) => {
        if (row.expression && row.expression.trim()) {
          expressionToAlias[row.expression.trim().toLowerCase()] = row.alias;
        }
      });

      const headerMap = {};
      ast.columns.forEach((col) => {
        const exprSql = this.sqlifySelectColumnExpr(col).trim();
        const alias = expressionToAlias[exprSql.toLowerCase()];
        if (alias) {
          col.as = alias;
          headerMap[exprSql] = alias;
        }
      });

      let newSql = sqlParser.sqlify(ast, { database: "PostgreSQL" });
      newSql = this.restoreFwSqlPlaceholders(newSql, sql);
      return { newSql, headerMap };
    },

    async generatePreview() {
      this.previewLoading = true;
      this.previewError = "";
      this.previewErrorQuery = "";
      let fetchSql = "";
      try {
        let sqlQuery = "";
        let result;

        if (this.isTableDataSource) {
          const built = this.buildTableModeSqlAndHeaderMap();
          if (!built || !built.newSql) {
            this.showToast("Select a table and at least one column.", "warning");
            return;
          }
          result = { newSql: built.newSql, headerMap: built.headerMap };
          if (!this.validateSQL(built.newSql)) {
            return;
          }
        } else {
          if (this.dataSqlId) {
            sqlQuery = this.dataSqlId;
          }
          if (!this.validateSQL(sqlQuery)) {
            return;
          }
          if (this.vizFunction === "tableBarChart") {
            result = this.buildTableBarPreview(sqlQuery);
          } else {
            result = this.buildPreviewSQL(sqlQuery);
          }
        }

        this.defaultChartData.reportHeaderColumnDp = result.headerMap;

        const previewSql = this.appendDynamicClause(result.newSql);
        this.defaultChartData.datasql = previewSql;
        fetchSql = this.appendPreviewLimit(previewSql);

        const controller = apiAbort.new();
        const data = {
          query: fetchSql,
          vizClassName: "CommonVizClass",
        };

        console.log("query", fetchSql, this.defaultChartData, result);

        const resp = await axios.post("/api/fetchData", data, {
          signal: controller.signal,
          skipGlobalDataErrorModal: true,
        });
        this.previewChartsVisible = true;
        await this.$nextTick();
        this.previewLoading = false;
        await this.$nextTick();
        const divId = this.$refs.r1c2;
        if (divId) {
          this.$commonFn.renderChartCompare(
            "CommonVizClass",
            this.defaultChartData,
            divId,
            this.vizFunction,
            resp.data,
            {},
          );
        }
      } catch (e) {
        const info = classifyPreviewOrFetchError(e);
        this.previewError = info.message;
        const apiQuery = extractFailedFetchQueryFromError(e);
        this.previewErrorQuery =
          apiQuery || (this.isTableDataSource ? fetchSql || "" : "");
        console.error("generatePreview", e);
        this.$bvToast.toast(info.message, {
          title: info.title,
          variant: "danger",
          solid: true,
          autoHideDelay: 7000,
        });
      } finally {
        this.previewLoading = false;
      }
    },
    async addNewParentMenu() {
      try {
        // Call your API to add new parent menu
      } catch (error) {
        console.error("Error adding parent menu:", error);
        // Handle error (show toast/message)
      }
    },
    submitForm() {
      this.$refs.observer.validate().then((valid) => {
        if (valid) {
          this.postConfigMenu();
        } else {
          this.$bvToast.toast("Please fill all required fields", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
        }
      });
    },
    menuConfigApiErrorMessage(error) {
      const data = error && error.response && error.response.data;
      if (data) {
        if (typeof data.details === "string" && data.details.trim()) {
          return data.details.trim();
        }
        if (typeof data.error === "string" && data.error.trim()) {
          return data.error.trim();
        }
      }
      if (error && error.message) return error.message;
      return "Please try again. If the problem persists, contact support.";
    },
    parseCreateSubMenuResult(resp) {
      const result = resp && resp.data && resp.data.result;
      const fnResult = result && result.fn_create_sub_menu;
      if (!fnResult) return null;

      const raw = fnResult.value != null ? fnResult.value : fnResult;
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (e) {
          console.warn("Unable to parse fn_create_sub_menu response", e, raw);
          return null;
        }
      }
      if (raw && typeof raw === "object") return raw;
      return null;
    },
    openVizScreenInNewTab(parentMenuId, menuId) {
      const route = this.$router.resolve({
        path: `/viz-screen/${parentMenuId}/${menuId}`,
      });
      const base = `${window.location.origin}${window.location.pathname}`;
      const href = route.href || `#/viz-screen/${parentMenuId}/${menuId}`;
      const fullUrl = href.startsWith("#") ? `${base}${href}` : `${base}#${href}`;
      console.log("fullUrl", fullUrl);
      window.open(fullUrl, "_blank", "noopener,noreferrer");
    },
    async reloadApplicationState() {
      this.$store.state.stateLoaded = false;
      try {
        await this.$store.dispatch("loadState");
      } catch (e) {
        console.warn("Failed to reload application state after save", e);
      }
    },
    resetPreviewWorkspaceState() {
      this.resetDataSourceSwitchState();
      this.applyDefaultDataSourceMode();
    },
    hydrateFormFromDefaults() {
      const row = this.fetchDefaultvalues && this.fetchDefaultvalues[0];
      if (!row) {
        this.resetForm();
        return;
      }
      this.menuName = this.stripMenuNamePrefix(
        row.screenletTitleDpName ||
          row.screenTitleDpName ||
          this.defaultMenuName ||
          "",
      );
      this.screenName = row.screenTitleDpName || row.screenletTitleDpName || "";
      this.filterSqlId = row.screenletListbox1_sqlId || row.screenListbox1_sqlId || "";
      this.filterSyspk = Array.isArray(this.defaultFilterSyspk)
        ? this.defaultFilterSyspk.join(",")
        : String(this.defaultFilterSyspk || "");
      this.dataSqlId = row.datasql || "";
      this.defaultChartData.datasql = row.datasql || "";
      this.vizTypeName = row.viztypeName || "";
      this.x_axisName = row.x1_axisDisplayName || "";
      this.y_axisName = row.y1_axisDisplayName || "";
      this.vizTitleName = row.vizletTitleDpName || "";
      this.x_axisTooltip = row.x1_metricTooltip || "";
      this.y_axisTooltip = row.y1_metricTooltip || "";
      this.x_axisDataName = row.x1_dataFieldName || "";
      this.y_axisDataName = row.y1_dataFieldName || "";
      const headerMap = row.reportHeaderColumnDp || {};
      this.defaultChartData.reportHeaderColumnDp = headerMap;
      this.vizParamJson = this.parseVizParamJsonFromRow(row);
      const savedMode = normalizeDataSourceMode(this.vizParamJson);
      if (savedMode) {
        this.dataSourceMode = savedMode;
      }
      if (this.vizFunction === "tableBarChart") {
        this.syncTableBarPreviewRowsFromSql(this.dataSqlId);
      } else {
        this.tableHeaders = Object.entries(headerMap).map(([header, value]) => ({
          id: this.nextFieldId++,
          header,
          value: String(value ?? ""),
        }));
      }
      this.initPreviewMapping();
    },
    async refreshMenuConfigPage() {
      await this.reloadApplicationState();
      this.resetPreviewWorkspaceState();
      this.hydrateFormFromDefaults();
      this.initMenuDefaults(this.activeMenuId);
      await this.$nextTick();
      await this.renderExistingChart();
      if (this.$refs.observer) {
        this.$refs.observer.reset();
      }
    },
    postConfigMenu() {
      let jsonString;
      if (this.vizFunction === "tableBarChart") {
        // Pass the headerMap we use for TableBar (reportHeaderColumnDp)
        const headerMap =
          this.defaultChartData.reportHeaderColumnDp ||
          this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp ||
          {};
        jsonString = JSON.stringify(headerMap);
        console.log("postConfigMenu tableHeaders (headerMap)", headerMap);
      } else {
        const transformedHeaders = this.tableHeaders.reduce((acc, current) => {
          acc[current.header] = current.value;
          return acc;
        }, {});
        jsonString = JSON.stringify(transformedHeaders);
        console.log("postConfigMenu tableHeaders", this.tableHeaders);
      }
      const existing = this.fetchDefaultvalues?.[0];
      const { listboxSql, listboxColumnMapping } =
        this.buildListboxPersistPayload();
      const persistDataSql = this.buildPersistDataSql();

      const formData = {
        // parentMenuId: this.parentMenuId.id,
        menuName: this.menuName,
        screenName: this.screenName,
        filterSqlId: this.filterSqlId,
        filterSyspk: this.filterSyspk,
        dataSql: persistDataSql,
        vizTypeName: existing?.viztypeName ?? this.vizTypeName,
        vizletId: existing?.vizletId ?? this.vizletId,
        x_axisName: this.x_axisName,
        y_axisName: this.y_axisName,
        vizTitleName: this.vizTitleName,
        x_axisTooltip: this.x_axisTooltip,
        y_axisTooltip: this.y_axisTooltip,
        x_axisDataName: this.x_axisDataName,
        y_axisDataName: this.y_axisDataName,
        x1_d3scaleType: existing?.x1_d3scaleType ?? this.x1_d3scaleType,
        tableHeaders: jsonString,
        listboxSql,
        listboxColumnMapping,
        vizParamJson: JSON.stringify(this.vizParamJson || {}),
        dataSourceMode: this.dataSourceMode,
      };
      console.log("form data", formData);
      this.menuSaving = true;
      axios
        .post("/api/sp-config-menu", formData)
        .then(async (resp) => {
          if (resp.status !== 200) return;

          const parsed = this.parseCreateSubMenuResult(resp);
          if (!parsed || String(parsed.status).toLowerCase() !== "success") {
            this.$bvToast.toast(
              (parsed && parsed.message) ||
                "Menu save did not complete successfully.",
              {
                title: "Failed to Save Menu",
                variant: "danger",
                solid: true,
                autoHideDelay: 9000,
              },
            );
            return;
          }

          const menuId = parsed.menu_id;
          if (!menuId) {
            this.$bvToast.toast("Menu saved but no menu id was returned.", {
              title: "Save incomplete",
              variant: "warning",
              solid: true,
            });
            return;
          }

          this.$bvToast.toast(
            "Menu has been successfully configured and saved.",
            {
              title: "Success",
              variant: "success",
              solid: true,
            },
          );

          this.openVizScreenInNewTab("my_dashboard", menuId);
          await this.reloadApplicationState();
          this.resetDataSourceSwitchState();
        })
        .catch((e) => {
          console.error("sp-config-menu", e);
          this.$bvToast.toast(this.menuConfigApiErrorMessage(e), {
            title: "Failed to Save Menu",
            variant: "danger",
            solid: true,
            autoHideDelay: 9000,
          });
        })
        .finally(() => {
          this.menuSaving = false;
        });
    },
    resetForm() {
      // Reset all form fields to their initial values
      this.parentMenuId = "";
      this.menuId = "";
      this.menuName = "";
      this.screenName = "";
      this.filterSqlId = "";
      this.filterSyspk = "";
      this.dataSqlId = "";
      this.vizTypeName = "";
      this.x_axisName = "";
      this.y_axisName = "";
      this.vizTitleName = "";
      this.x_axisTooltip = "";
      this.y_axisTooltip = "";
      this.x_axisDataName = "";
      this.y_axisDataName = "";
      this.tableHeaders = []; // Reset to default if you have one
    },
    addField() {
      this.tableHeaders.push({
        id: this.nextFieldId++, // Assign a unique ID for :key
        header: "",
        value: "",
      });
    },
    removeField(index) {
      this.tableHeaders.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.menu-name-prefix-input {
  position: relative;
}

.menu-name-prefix-input__prefix {
  position: absolute;
  left: 0.75rem;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  color: #adb5bd;
  pointer-events: none;
  z-index: 2;
  user-select: none;
  white-space: nowrap;
}

.menu-name-prefix-input__field {
  padding-left: 8.35rem;
}

.menu-config-text-input,
.menu-config-text-input.form-control {
  user-select: text !important;
  -webkit-user-select: text !important;
}

body.fw-selection-locked .menu-config-text-input,
body.fw-selection-locked .menu-config-text-input.form-control {
  user-select: text !important;
  -webkit-user-select: text !important;
}

.preview-setup-card input.form-control,
.preview-setup-card textarea.form-control {
  user-select: text !important;
  -webkit-user-select: text !important;
}

body.fw-selection-locked .preview-setup-card input.form-control,
body.fw-selection-locked .preview-setup-card textarea.form-control,
body.fw-selection-locked .fw-copyable-text,
body.fw-selection-locked .fw-copyable-text * {
  user-select: text !important;
  -webkit-user-select: text !important;
}

/* Standalone menu-config scroll container.
   Disabled when used with :embedded=\"true\" so vizlet can control scrolling. */
.menu-page-scroll {
  max-height: calc(100vh - 80px);
  overflow: auto;
  padding: 12px 14px;
  width: 100%;
}

.preview-setup-card {
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.07);
}

.preview-setup-body {
  padding: clamp(0.35rem, 0.8vw, 0.65rem);
}

.preview-workflow-card {
  border: 1px solid rgba(13, 110, 253, 0.12);
  background: linear-gradient(180deg, #fbfcfe 0%, #fff 36px);
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.05);
}

.preview-workflow-hero__title {
  font-size: 1rem;
  letter-spacing: -0.02em;
  color: #1a1d21;
}

.preview-workflow-hero__sub {
  font-size: 0.72rem;
  line-height: 1.35;
  margin-top: 0.2rem;
}

.preview-workflow-section {
  padding: 0.25rem 0.35rem 0.3rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.preview-workflow-section--charts {
  background: linear-gradient(
    145deg,
    rgba(13, 110, 253, 0.05) 0%,
    rgba(255, 255, 255, 0.95) 42%
  );
}

.preview-workflow-section--mapping {
  background: #fff;
}

.preview-section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
}

.preview-section-heading--data-source {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 0.75rem;
  row-gap: 0.4rem;
}

.data-source-toolbar__start {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
}

.data-source-toolbar__center {
  display: flex;
  justify-content: center;
  min-width: 0;
}

.data-source-toolbar__hint {
  font-size: 0.65rem;
  line-height: 1.35;
  text-align: right;
  max-width: 22rem;
  justify-self: end;
}

.preview-section-heading .setup-step-badge {
  margin-top: 0.1rem;
}

.preview-section-heading__title {
  font-weight: 700;
  font-size: 0.82rem;
  color: #343a40;
  line-height: 1.25;
}

.preview-section-heading__hint {
  margin-top: 0.12rem;
  max-width: 42rem;
  line-height: 1.35;
  font-size: 0.72rem;
}

.preview-section-heading__action {
  flex-shrink: 0;
  align-self: flex-start;
  margin-left: auto;
}

.preview-charts--panel {
  display: grid;
  gap: 0.55rem;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
}

.preview-charts--single-preview {
  grid-template-columns: 1fr;
}

.chart-panel {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
  min-height: 0;
  overflow: hidden;
}

.chart-panel--existing {
  border-color: rgba(108, 117, 125, 0.22);
}

.chart-panel--preview {
  border-color: rgba(13, 110, 253, 0.2);
}

.chart-panel__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  padding: 0.3rem 0.5rem;
  background: linear-gradient(180deg, #f8f9fb 0%, #f1f3f5 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.chart-panel__title {
  font-size: 0.68rem;
  font-weight: 700;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.chart-panel__badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
}

.chart-panel__surface {
  flex: 1;
  padding: 0.4rem 0.5rem 0.5rem;
  display: flex;
  flex-direction: column;
  min-height: 120px;
}

.chart-panel--table-bar .chart-panel__surface {
  min-height: 160px;
}

.chart-panel__canvas {
  flex: 1;
  min-height: 96px;
}

.chart-panel__loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 0.5rem;
}

.preview-chart-pending {
  padding: 0.45rem 0.65rem;
  border-radius: 8px;
  border: 1px dashed rgba(13, 110, 253, 0.28);
  background: rgba(13, 110, 253, 0.04);
}

.preview-chart-pending__inner {
  display: block;
  line-height: 1.4;
  font-size: 0.72rem;
}

.btn-generate-preview {
  font-weight: 600;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(23, 162, 184, 0.28);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.btn-generate-preview:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(23, 162, 184, 0.34);
}

@media (max-width: 576px) {
  .preview-workflow-section {
    padding: 0.5rem 0.55rem;
  }
}

.setup-step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.35rem;
  height: 1.35rem;
  padding: 0 0.28rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #0d6efd, #4dabf7);
  color: #fff;
  font-size: 0.68rem;
  flex-shrink: 0;
}

.setup-step-badge--muted {
  background: #e9ecef;
  color: #495057;
}

.preview-setup-main {
  margin-top: 0.15rem;
}

.preview-setup-main--table-catalog {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 0;
}

.preview-setup-main--catalog-hidden .preview-main-col {
  flex: 0 0 100%;
  max-width: 100%;
}

.preview-setup-main--table-catalog .preview-main-col {
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
}

.preview-main-col {
  min-width: 0;
  width: 100%;
}

.preview-setup-main:not(.preview-setup-main--table-catalog) .preview-main-col {
  flex: 0 0 100%;
  max-width: 100%;
}

.preview-catalog-col {
  position: relative;
  flex-shrink: 0;
  min-width: 0;
  padding-right: 6px;
}

.preview-catalog-col--resizing {
  user-select: none;
}

.preview-catalog-col--resizing .catalog-tree__copyable {
  user-select: none;
}

.catalog-resize-handle {
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 3;
  touch-action: none;
}

.catalog-resize-handle::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  width: 2px;
  border-radius: 2px;
  background: transparent;
  transition: background 0.12s ease;
}

.catalog-resize-handle:hover::after,
.preview-catalog-col--resizing .catalog-resize-handle::after {
  background: rgba(13, 110, 253, 0.45);
}

.catalog-restore-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
  padding: 0.3rem 0.55rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  color: #495057;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.catalog-restore-btn:hover {
  background: #f8f9fa;
  border-color: #ced4da;
  color: #0d6efd;
}

.catalog-restore-btn__icon {
  font-size: 0.85rem;
  line-height: 1;
}

.catalog-tree {
  max-height: min(52vh, 420px);
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  transition: max-height 0.2s ease;
}

.catalog-tree--collapsed {
  max-height: none;
}

.catalog-tree__header {
  padding: 0.35rem 0.45rem;
  background: linear-gradient(180deg, #fafbfc 0%, #f1f3f5 100%);
  border-bottom: 1px solid #e9ecef;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  min-width: 0;
}

.catalog-tree__search {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 0.72rem;
  border-radius: 6px;
  height: calc(1.5em + 0.5rem + 2px);
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
}

.catalog-tree__header-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
}

.catalog-tree__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.55rem;
  height: 1.55rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.catalog-tree__action-btn:hover {
  background: rgba(13, 110, 253, 0.08);
  border-color: rgba(13, 110, 253, 0.15);
  color: #0d6efd;
}

.catalog-tree__action-icon {
  display: inline-block;
  font-size: 1.2rem;
  line-height: 1;
  transition: transform 0.15s ease;
}

.catalog-tree__action-icon--vertical.is-collapsed {
  transform: rotate(-90deg);
}

.catalog-tree__action-icon--hide {
  font-size: 1.2rem;
}

.catalog-tree__title {
  flex-shrink: 0;
  font-weight: 700;
  font-size: 0.8rem;
  color: #495057;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-align: left;
}

.catalog-tree__empty {
  font-size: 0.72rem;
}

.catalog-tree__body {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(min(52vh, 420px) - 2.6rem);
  padding: 0.2rem 0 0.4rem;
}

.catalog-tree__row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.25rem;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0.28rem 0.5rem 0.28rem 0.35rem;
  font-size: 0.8rem;
  line-height: 1.2;
  color: #343a40;
  border-radius: 6px;
  transition: background 0.12s ease;
  text-align: left;
}

.catalog-tree__toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  border-radius: 4px;
  flex-shrink: 0;
}

.catalog-tree__toggle-btn:hover {
  background: rgba(13, 110, 253, 0.06);
}

.catalog-tree__toggle-btn--table {
  padding: 0.1rem 0.15rem;
}

.catalog-tree__row--table:hover {
  background: rgba(13, 110, 253, 0.06);
}

.catalog-tree__row--schema {
  font-weight: 600;
  padding-left: 0.4rem;
}

.catalog-tree__row--table {
  font-weight: 500;
  font-size: 0.78rem;
  cursor: pointer;
}

.catalog-tree__row--table.is-active {
  background: rgba(13, 110, 253, 0.14);
  color: #0b5ed7;
}

.catalog-tree__row--table .catalog-tree__copyable {
  cursor: pointer;
}

.catalog-tree__nested {
  margin-left: 0.35rem;
  padding-left: 0.4rem;
  border-left: 1px solid #e9ecef;
}

.catalog-tree__table-head {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.catalog-tree__chevron-btn {
  border: none;
  background: transparent;
  padding: 0 0.15rem 0 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #868e96;
  border-radius: 4px;
}

.catalog-tree__chevron-btn:hover {
  color: #495057;
  background: rgba(0, 0, 0, 0.04);
}

.catalog-tree__chevron {
  display: inline-block;
  transition: transform 0.15s ease;
  font-size: 1.2rem;
  line-height: 1;
  width: 0.75rem;
  text-align: center;
}

.catalog-tree__chevron--sm {
  font-size: 1.2rem;
}

.catalog-tree__chevron.is-open {
  transform: rotate(90deg);
}

.catalog-tree__ico {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 2px;
  margin-right: 0.15rem;
}

.catalog-tree__ico--schema {
  background: linear-gradient(135deg, #748ffc, #9775fa);
  opacity: 0.95;
}

.catalog-tree__ico--table {
  background: linear-gradient(135deg, #4dabf7, #339af0);
  opacity: 0.95;
}

.catalog-tree__label {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.catalog-tree__copyable {
  user-select: text;
  -webkit-user-select: text;
  cursor: text;
}

.catalog-tree__columns {
  margin: 0.15rem 0 0.35rem 1.5rem;
  padding: 0.2rem 0 0.25rem 0.45rem;
  border-left: 1px dashed #dee2e6;
}

.catalog-tree__col {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.35rem;
  font-size: 0.72rem;
  color: #495057;
  padding: 0.12rem 0;
  line-height: 1.2;
  text-align: left;
}

.catalog-tree__col-name {
  flex: 1;
  min-width: 0;
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  text-align: left;
}

.catalog-tree__col-type {
  flex: 0 0 auto;
  max-width: 5.5rem;
  font-size: 0.65rem;
  color: #868e96;
}

.catalog-tree__type-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.catalog-tree__type-dot--text {
  background: #51cf66;
}

.catalog-tree__type-dot--date {
  background: #ff922b;
}

.catalog-tree__type-dot--num {
  background: #339af0;
}

.preview-chart-placeholder {
  flex: 1;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.65rem;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 6px;
  border: 1px dashed rgba(13, 110, 253, 0.22);
}

.preview-chart-placeholder__icon {
  opacity: 0.45;
  margin-bottom: 0.35rem;
}

.scrollmenu {
  max-height: 200px;
  overflow: auto;
}

.mapping-table code {
  font-size: 0.85em;
  color: #495057;
  background: #f8f9fa;
  padding: 2px 5px;
  border-radius: 3px;
}

/* Preview card: allow cols and content to shrink */
.preview-col-wrap {
  min-width: 0;
}

.preview-sql-row .sql-form-group {
  margin-bottom: 0.5rem;
}

.preview-workflow-section--mapping .mapping-table-wrap {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafbfc;
  padding: 0.22rem;
}

.preview-workflow-section--mapping .mapping-table {
  background: #fff;
}

.preview-sql-row .sql-form-group >>> textarea,
.preview-workflow-card .sql-textarea,
.preview-setup-card .sql-textarea {
  font-size: 11px;
  padding: 4px 7px;
  width: 100%;
  box-sizing: border-box;
  max-height: 5rem;
  overflow-y: auto !important;
  line-height: 1.35;
}

.mapping-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
}

.mapping-table-wrap--table-bar {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Search bar layout: see variable-mapping.css (VariableMappingPanel) */

.mapping-table-scroll-body {
  max-height: min(52vh, 420px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.mapping-table-wrap--table-bar .mapping-table-scroll-body .mapping-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8f9fa;
  box-shadow: 0 1px 0 #dee2e6;
}

.mapping-table-wrap .mapping-table.mapping-table-wide {
  min-width: 720px;
}

.column-name-readonly {
  font-size: 0.85em;
  color: #495057;
  background: #f8f9fa;
  padding: 2px 5px;
  border-radius: 3px;
}

.data-source-segment {
  display: inline-flex;
  align-items: stretch;
  padding: 3px;
  border-radius: 8px;
  background: linear-gradient(180deg, #eceff3 0%, #dfe4ea 100%);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.07);
  gap: 2px;
}

.segment-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  border: none;
  cursor: pointer;
  padding: 0.24rem 0.45rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.65rem;
  color: #5a6570;
  background: transparent;
  transition: color 0.15s ease, background 0.15s ease, box-shadow 0.2s ease,
    transform 0.12s ease;
}

.segment-btn:hover {
  color: #343a40;
  background: rgba(255, 255, 255, 0.55);
}

.segment-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.28);
}

.segment-btn.active {
  color: #0b5ed7;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 6px 16px rgba(13, 110, 253, 0.15);
}

.segment-btn__icon {
  display: flex;
  opacity: 0.92;
}

.segment-btn.active .segment-btn__icon {
  opacity: 1;
  color: #0d6efd;
}

.segment-btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.segment-btn:disabled:hover {
  color: #5a6570;
  background: transparent;
}

@media (max-width: 767.98px) {
  .preview-section-heading--data-source {
    grid-template-columns: 1fr;
    justify-items: stretch;
  }

  .data-source-toolbar__center {
    justify-content: stretch;
  }

  .preview-section-heading--data-source .data-source-segment {
    width: 100%;
    justify-content: stretch;
  }

  .preview-section-heading--data-source .segment-btn {
    flex: 1;
    justify-content: center;
  }

  .data-source-toolbar__hint {
    text-align: left;
    max-width: none;
    justify-self: stretch;
  }
}

.table-picker-row .table-picker-select {
  border-radius: 6px;
}

.table-path-pill {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  background: linear-gradient(135deg, #f0f5ff 0%, #e8f0fe 100%);
  border: 1px solid rgba(13, 110, 253, 0.2);
  font-size: 0.8rem;
  font-weight: 600;
  font-family: "Consolas", "Monaco", "Courier New", monospace;
}

.table-path-pill__schema {
  color: #5c6bc0;
}

.table-path-pill__sep {
  margin: 0 0.2rem;
  color: #adb5bd;
  font-weight: 400;
}

.table-path-pill__table {
  color: #0d47a1;
}

.column-name-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.column-include-cb {
  flex-shrink: 0;
}

.column-name-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}

.column-include-cb--header {
  margin-top: 0;
}

.type-select--readonly:disabled {
  background-color: #f8f9fa;
  color: #495057;
  cursor: not-allowed;
  opacity: 1;
}

.aggregate-cell--inactive {
  background: #f1f3f5;
}

.aggregate-cell--inactive .type-select:disabled {
  background-color: #e9ecef;
  color: #868e96;
  cursor: not-allowed;
  opacity: 0.85;
}

.graph-cell--inactive {
  background: #f1f3f5;
}

.graph-cell--inactive .type-select:disabled {
  background-color: #e9ecef;
  color: #868e96;
  cursor: not-allowed;
  opacity: 0.85;
}

.column-name-readonly--inline {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mapping-table-wrap .mapping-table th,
.mapping-table-wrap .mapping-table td {
  padding: 4px 6px;
  white-space: nowrap;
}

.mapping-table-wrap .mapping-table td:last-child {
  white-space: normal;
  word-break: break-word;
  max-width: 180px;
}

/* Responsive: shrink on smaller screens */
@media (max-width: 1400px) {
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-workflow-card .sql-textarea {
    font-size: 10px;
    padding: 3px 6px;
    max-height: 4.75rem;
  }
  .mapping-table-wrap .mapping-table {
    font-size: 12px;
  }
  .mapping-table-wrap .mapping-table th,
  .mapping-table-wrap .mapping-table td {
    padding: 3px 5px;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 140px;
  }
}

@media (max-width: 992px) {
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-workflow-card .sql-textarea {
    font-size: 10px;
    max-height: 4.5rem;
  }
  .mapping-table-wrap .mapping-table {
    font-size: 11px;
  }
  .mapping-table-wrap .mapping-table th,
  .mapping-table-wrap .mapping-table td {
    padding: 2px 4px;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 120px;
  }
}

@media (max-width: 768px) {
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-workflow-card .sql-textarea {
    font-size: 10px;
    padding: 3px 5px;
    max-height: 4.25rem;
  }
  .preview-mapping-row .mapping-table-wrap {
    margin-bottom: 0.5rem;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 100%;
  }
}

.inline-mapping-input {
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 10px;
  color: #495057;
  background: #f8f9fa;
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 1px 5px;
  width: 100%;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}

.inline-mapping-input:focus {
  background: #fff;
  border-color: #80bdff;
}

.type-select.custom-select {
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 10px;
  color: #495057;
  /* keep Bootstrap's default background-image (caret) */
  background-color: #f8f9fa;
  border-radius: 3px;
  padding: 2px 5px;
  width: 100%;
  height: calc(1.5em);
  line-height: 1;
}

.inline-mapping-input::placeholder {
  color: #adb5bd;
  font-style: italic;
}

.preview-btn-row {
  display: flex;
  justify-content: flex-end;
  padding: 0;
  line-height: 1;
  margin-top: 0;
}

.preview-btn-row--setup {
  margin-top: 0.45rem;
  margin-bottom: 0;
}

.preview-btn {
  font-size: 14px;
  background: #17a2b8;
  color: #fff;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.15s;
}

.preview-btn:hover:not(:disabled) {
  background: #138496;
}

.preview-btn:disabled {
  background: #b0b7bc;
  cursor: not-allowed;
  opacity: 0.8;
}

.preview-fetch-error {
  user-select: text !important;
  -webkit-user-select: text !important;
  cursor: text;
}

.preview-fetch-error-query {
  font-size: 0.75rem;
  user-select: text !important;
  -webkit-user-select: text !important;
  cursor: text;
  line-height: 1.35;
  max-height: 12rem;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  background: rgba(0, 0, 0, 0.04);
  padding: 0.5rem 0.65rem;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
